import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Stack,
    Collapse,
    IconButton,
    Card,
    CardContent,
    FormControlLabel,
    Checkbox,
    Divider,
    RadioGroup,
    Radio,
    FormLabel,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Tabs,
    Tab,
    Chip,
    Autocomplete,
    Tooltip,
    Switch,
    Snackbar
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import { useVehicleType } from '../VehicleTypeSelector';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CheckCircle, ExpandMore, ExpandLess, DirectionsCar, Security, Assignment, ErrorOutline, Refresh as RefreshIcon, PhotoCamera, DeleteOutline } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api';
import CityStateAutocomplete from './CityStateAutocomplete';
import ReceiptAttachmentGallery from './ReceiptAttachmentGallery';
import {
    TARP_VALUES,
    normalizeTarpValue,
    formatTarpTotal,
    sumTarps,
} from '../../utils/tarpMetrics';
import samsaraPollingService from '../../services/samsaraPollingService';
import { mapSamsaraVehicleType } from '../../utils/samsaraVehicleType';

/** Track visualViewport so phone zoom / Display Zoom don't clip fixed-width controls. */
function useVisualViewport() {
    const [state, setState] = useState(() => {
        if (typeof window === 'undefined') return { width: 1200, scale: 1 };
        const vv = window.visualViewport;
        return {
            width: vv?.width ?? window.innerWidth,
            scale: typeof vv?.scale === 'number' ? vv.scale : 1,
        };
    });
    useEffect(() => {
        const update = () => {
            const vv = window.visualViewport;
            setState({
                width: vv?.width ?? window.innerWidth,
                scale: typeof vv?.scale === 'number' ? vv.scale : 1,
            });
        };
        update();
        const vv = window.visualViewport;
        vv?.addEventListener('resize', update);
        vv?.addEventListener('scroll', update);
        window.addEventListener('resize', update);
        window.addEventListener('orientationchange', update);
        return () => {
            vv?.removeEventListener('resize', update);
            vv?.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            window.removeEventListener('orientationchange', update);
        };
    }, []);
    return state;
}

function breakdownRowIsEmpty(ev) {
    if (!ev) return true;
    const dateMissing = !ev.date || Number.isNaN(new Date(ev.date).getTime());
    const timeMissing = !String(ev.notificationTime || '').trim();
    return dateMissing && timeMissing;
}

function normalizeWeekendPay(value) {
    return value === 'half' || value === 'full' ? value : 'none';
}

function breakdownRowIsComplete(ev) {
    if (!ev) return false;
    const dateOk = ev.date && !Number.isNaN(new Date(ev.date).getTime());
    const timeOk = /^\d{1,2}:\d{2}/.test(String(ev.notificationTime || '').trim());
    return Boolean(dateOk && timeOk);
}

/** Hide decommissioned units (unit/name contains "old", any casing). */
function isRetiredTruckLabel(value) {
    return String(value || '').toLowerCase().includes('old');
}

function truckDisplayUnit(truck) {
    if (!truck) return '';
    return String(truck.unitNumber || truck.truckNumber || truck.name || '').trim();
}

function excludeRetiredTrucks(list) {
    if (!Array.isArray(list)) return [];
    return list.filter((t) => !isRetiredTruckLabel(truckDisplayUnit(t)));
}

/**
 * Autocomplete search: digit queries match unit number (not year).
 * e.g. "200" → truck 200, not year 2022 Peterbilt.
 */
function filterTruckOptions(options, inputValue) {
    const searchTerm = String(inputValue || '').trim().toLowerCase();
    if (!searchTerm) return options;

    const digitOnly = /^\d+$/.test(searchTerm);

    const scored = [];
    for (const option of options) {
        const unit = truckDisplayUnit(option).toLowerCase();
        const unitDigits = unit.replace(/\D+/g, '');
        const make = String(option.make || '').toLowerCase();
        const model = String(option.model || '').toLowerCase();
        const vin = String(option.vin || '').toLowerCase();

        if (digitOnly) {
            // Prefer exact / leading unit number; never match year for digit-only search
            const exact = unit === searchTerm || unitDigits === searchTerm;
            const unitStarts = unit.startsWith(searchTerm) || unitDigits.startsWith(searchTerm);
            // Allow "200" in "200A" or "Unit 200" via word/digit boundary
            const unitHasNumber = new RegExp(`(^|\\D)${searchTerm}(\\D|$)`).test(unit)
                || unitDigits === searchTerm;
            if (!exact && !unitStarts && !unitHasNumber) continue;
            scored.push({ option, rank: exact ? 0 : unitStarts ? 1 : 2 });
            continue;
        }

        const year = String(option.year || '').toLowerCase();
        const hay = `${unit} ${make} ${model} ${vin} ${year}`;
        if (!hay.includes(searchTerm)) continue;
        const exactUnit = unit === searchTerm;
        scored.push({ option, rank: exactUnit ? 0 : 1 });
    }

    scored.sort((a, b) => a.rank - b.rank);
    return scored.map((s) => s.option);
}

/** Calendar YYYY-MM-DD in the user's local timezone. */
function toLocalDateKey(date) {
    if (!date) return null;
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Stable week-start Date for API filter/upsert (UTC noon of local calendar day). */
function toWeekStartCanonical(date) {
    const key = toLocalDateKey(date);
    if (!key) return null;
    return new Date(`${key}T12:00:00.000Z`);
}

/** Parse odometer / numeric fields (strip commas, trim). */
function parseOdometerInput(val) {
    if (val === null || val === undefined) return NaN;
    return parseFloat(String(val).replace(/,/g, '').trim());
}

/** Format a timestamp as a short relative string (e.g. "5s ago", "12m ago"). */
function formatRelativeTime(date) {
    if (!date) return '';
    const ms = Date.now() - new Date(date).getTime();
    if (ms < 0) return 'just now';
    const sec = Math.floor(ms / 1000);
    if (sec < 5) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `${day}d ago`;
}

/** Pull a truck id from a stored ref (string, ObjectId, or populated { _id }). */
function truckRefId(value) {
    if (value == null || value === '') return '';
    if (typeof value === 'object') return String(value._id || value.id || '');
    return String(value);
}

function samsaraKeyFromRef(value) {
    const sid = truckRefId(value);
    if (!sid) return '';
    return sid.startsWith('samsara-') ? sid.slice('samsara-'.length) : '';
}

/** True when a saved draft/checklist id refers to this dropdown option (Mongo id or samsara-*). */
function truckOptionMatchesRef(truck, savedId) {
    if (!truck) return false;
    const sid = truckRefId(savedId);
    if (!sid) return false;
    if (String(truck._id) === sid) return true;
    if (truck.samsaraId && (sid === String(truck.samsaraId) || sid === `samsara-${truck.samsaraId}`)) return true;
    const optionKey = samsaraKeyFromRef(truck._id);
    const savedKey = samsaraKeyFromRef(sid);
    if (optionKey && (optionKey === sid || optionKey === savedKey)) return true;
    if (savedKey && String(truck.samsaraId) === savedKey) return true;
    return false;
}

/** Map a saved truck id onto the id used by the current dropdown options. */
function canonicalTruckOptionId(savedId, truckOptions) {
    const sid = truckRefId(savedId);
    if (!sid) return '';
    if (!Array.isArray(truckOptions) || truckOptions.length === 0) return sid;
    const match = truckOptions.find((t) => truckOptionMatchesRef(t, sid));
    return match ? match._id : sid;
}

function sameTruckRef(a, b) {
    const sa = truckRefId(a);
    const sb = truckRefId(b);
    if (!sa || !sb) return false;
    if (sa === sb) return true;
    const ka = samsaraKeyFromRef(sa) || sa;
    const kb = samsaraKeyFromRef(sb) || sb;
    if (samsaraKeyFromRef(sa) && (ka === sb || ka === kb)) return true;
    if (samsaraKeyFromRef(sb) && (kb === sa || ka === kb)) return true;
    return false;
}

/** Align odometer rows with multi-select truck order; preserve existing mileages when possible. */
function truckMileagesForSelection(selectedIds, prevRows = []) {
    return selectedIds.map((id) => {
        const prev = prevRows.find((r) => sameTruckRef(r.truckId, id));
        return prev
            ? { truckId: id, mileageStart: prev.mileageStart ?? '', mileageEnd: prev.mileageEnd ?? '' }
            : { truckId: id, mileageStart: '', mileageEnd: '' };
    });
}

/** Build mileage rows from saved checklist (truckMileages API or legacy truck + additionalTrucks + single odometer). */
function hydrateTruckMileagesFromChecklist(data) {
    if (!data) return [];
    const tm = data.truckMileages;
    if (Array.isArray(tm) && tm.length > 0) {
        return tm.map((row) => ({
            truckId: truckRefId(row.truck) || truckRefId(row.truckId),
            mileageStart: row.mileageStart != null && row.mileageStart !== '' ? String(row.mileageStart) : '',
            mileageEnd: row.mileageEnd != null && row.mileageEnd !== '' ? String(row.mileageEnd) : ''
        }));
    }
    const primary = truckRefId(data.truck);
    const adds = Array.isArray(data.additionalTrucks)
        ? data.additionalTrucks.map((t) => truckRefId(t)).filter(Boolean)
        : [];
    const order = primary
        ? [primary, ...adds.filter((a) => String(a) !== String(primary))]
        : adds;
    if (order.length === 0) return [];
    return order.map((id, idx) => ({
        truckId: id,
        mileageStart: idx === 0 && data.mileageStart != null ? String(data.mileageStart) : '',
        mileageEnd: idx === 0 && data.mileageEnd != null ? String(data.mileageEnd) : ''
    }));
}

function selectedTruckIdsFromChecklist(data) {
    const rows = hydrateTruckMileagesFromChecklist(data);
    if (rows.length > 0) return rows.map((r) => r.truckId).filter(Boolean);
    const primary = truckRefId(data.truck);
    const adds = Array.isArray(data.additionalTrucks)
        ? data.additionalTrucks.map((t) => truckRefId(t)).filter(Boolean)
        : [];
    return primary ? [primary, ...adds.filter((a) => String(a) !== String(primary))] : adds;
}

function hydrateTruckMileagesFromDraft(draft) {
    if (!draft) return [];
    if (Array.isArray(draft.truckMileages) && draft.truckMileages.length > 0) {
        return draft.truckMileages.map((row) => ({
            truckId: truckRefId(row.truck) || truckRefId(row.truckId),
            mileageStart: row.mileageStart != null && row.mileageStart !== '' ? String(row.mileageStart) : '',
            mileageEnd: row.mileageEnd != null && row.mileageEnd !== '' ? String(row.mileageEnd) : ''
        }));
    }
    const sel = Array.isArray(draft.selectedTrucks)
        ? draft.selectedTrucks.map((t) => truckRefId(t)).filter(Boolean)
        : [];
    if (sel.length > 0) {
        return sel.map((id, i) => ({
            truckId: id,
            mileageStart: i === 0 && draft.mileageStart != null ? String(draft.mileageStart) : '',
            mileageEnd: i === 0 && draft.mileageEnd != null ? String(draft.mileageEnd) : ''
        }));
    }
    const tid = truckRefId(draft.truck);
    return tid
        ? [
              {
                  truckId: tid,
                  mileageStart: draft.mileageStart != null ? String(draft.mileageStart) : '',
                  mileageEnd: draft.mileageEnd != null ? String(draft.mileageEnd) : ''
              }
          ]
        : [];
}

function selectedTrucksFromDraft(draft) {
    const rows = hydrateTruckMileagesFromDraft(draft);
    if (rows.length > 0) return rows.map((r) => r.truckId).filter(Boolean);
    const tid = truckRefId(draft.truck);
    return tid ? [tid] : [];
}

const WeeklyChecklistForm = ({ onSubmitSuccess, defaultOpen = false, compact = false, initialData = null }) => {
    const { user } = useAuth();
    const [formOpen, setFormOpen] = useState(Boolean(defaultOpen));
    const [loading, setLoading] = useState(false);
    const [trucks, setTrucks] = useState([]);
    const [samsaraConnected, setSamsaraConnected] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [info, setInfo] = useState('');
    const [draftId, setDraftId] = useState(null);
    const [draftLoaded, setDraftLoaded] = useState(false);
    const [autoSaveState, setAutoSaveState] = useState('idle'); // idle | saving | saved | error
    const [lastAutoSaveAt, setLastAutoSaveAt] = useState(null);
    // Disabled until draft load finishes and user edits (prevents empty overwrite)
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
    const [editId, setEditId] = useState(null);
    const [weeklyLoadsErrors, setWeeklyLoadsErrors] = useState([]); // [{date, eldOrderNumber, from, to}]
    const [highlightWeeklyLoads, setHighlightWeeklyLoads] = useState(false);
    const [mobileDay, setMobileDay] = useState(new Date().getDay());
    const [tarpsErrors, setTarpsErrors] = useState(Array(7).fill(false));
    const [shortRunYesErrors, setShortRunYesErrors] = useState(Array(7).fill(false));
    const [shortRunOrderErrors, setShortRunOrderErrors] = useState(Array(7).fill(false));
    const [trucksLoading, setTrucksLoading] = useState(true);
    const [trucksRefreshing, setTrucksRefreshing] = useState(false);
    const [trucksLastFetchedAt, setTrucksLastFetchedAt] = useState(null);
    const [includeStaleSamsara, setIncludeStaleSamsara] = useState(false);
    const [refreshNotice, setRefreshNotice] = useState(null); // { severity: 'success'|'info'|'error', text }
    const [receiptAttachments, setReceiptAttachments] = useState([]);
    const [receiptUploading, setReceiptUploading] = useState(false);
    const [receiptError, setReceiptError] = useState('');
    const [receiptRemovingId, setReceiptRemovingId] = useState(null);
    const receiptInputRef = React.useRef(null);
    const draftIdRef = React.useRef(null);
    const [, setNowTick] = useState(0);
    // Tick every 30s so the "Last updated Xs ago" label keeps updating.
    useEffect(() => {
        const id = setInterval(() => setNowTick((n) => n + 1), 30000);
        return () => clearInterval(id);
    }, []);
    useEffect(() => {
        if (compact) {
            setFormOpen(true);
        }
    }, [compact]);
    useEffect(() => {
        draftIdRef.current = draftId;
    }, [draftId]);

    
    const [formData, setFormData] = useState({
        truck: '',
        selectedTrucks: [],
        weekStartDate: null,
        weekEndDate: null,
        vehicleInspection: {
            brakes: 'pass',
            tires: 'pass',
            lights: 'pass',
            engine: 'pass',
            transmission: 'pass',
            steering: 'pass',
            mirrors: 'pass',
            windshield: 'pass'
        },
        safetyEquipment: {
            fireExtinguisher: 'present',
            firstAidKit: 'present',
            emergencyTriangles: 'present',
            tieDownEquipment: 'sufficient'
        },
        safetyQuestions: {
            hoursOfServiceCompliance: true,
            drugAlcoholPolicy: true,
            safetyTrainingCurrent: true,
            incidentsReported: true
        },
        truckMileages: [], // { truckId, mileageStart, mileageEnd } — same order as selectedTrucks
        fuelPurchases: '',
        maintenanceIssues: '',
        additionalNotes: '',
        
        // Operational details (new)
        oversizeLoadedMiles: '',
        dotInspectionCompleted: false,
        hasReimbursement: false,
        cashReceiptsAmount: '',
        cashReceiptsNote: '',
        hasBreakdownPay: false,
        breakdownEvents: [], // {date, notificationTime}
        detentionEvents: [], // {date, location, arrivalTime, departTime}
        weekendPay: 'none',
        weeklyLoads: [], // {date, eldOrderNumber, from, to}
        dailyMetrics: {
            nightsOut: Array(7).fill(false),
            tarps: Array(7).fill(''), // optional: '', '0', '1/2', '1', '1 1/2', '2' ('' displays as -)
            extraDropPickup: Array(7).fill(''),
            wwWoodCabinetDrops: Array(7).fill(''),
            preLoadNotDelivering: Array(7).fill(''),
            oversizeTruss: Array(7).fill(false),
            shortRunYes: Array(7).fill(''), // optional: '' | 'yes' | 'no' ('' displays as -)
            shortRunOrders: Array.from({ length: 7 }, () => []) // array of order numbers per day
        }
    });

    const theme = useTheme();
    const mqMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { width: vvWidth, scale: vvScale } = useVisualViewport();
    // Pinch-zoom / accessibility zoom shrinks visualViewport; treat as mobile + stack controls.
    const isZoomed = vvScale > 1.05 || (typeof window !== 'undefined' && window.innerWidth > 0 && vvWidth / window.innerWidth < 0.92);
    const isMobile = mqMobile || vvWidth < theme.breakpoints.values.md || isZoomed;
    const isTightLayout = isMobile || isZoomed || vvWidth < 480;
    const { isAll, isVans, isFlatbeds } = useVehicleType();
    const dayLabels = ['Sun','Mon','Tues','Wed','Thurs','Fri','Sat'];

    const scrollToMetric = (id) => {
        try {
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } catch (_) {}
    };

    useEffect(() => {
        console.log('[WeeklyChecklistForm] mount', {
            userRole: user?.role,
            userId: user?._id,
            userCompany: user?.company,
            userCompanyType: user?.company ? typeof user.company : 'n/a',
        });
        fetchTrucks();
        return () => {
            console.log('[WeeklyChecklistForm] unmount');
        };
    }, []);

    // Load item into form when editing
    useEffect(() => {
        if (!initialData) return;
        console.log('[WeeklyChecklistForm][Edit] Loading item into form', initialData?._id);
        setEditId(initialData._id);
        setFormOpen(true);
        setReceiptAttachments(Array.isArray(initialData.cashReceiptsAttachments) ? initialData.cashReceiptsAttachments : []);
        // Map initial data into form
        setFormData(prev => ({
            ...prev,
            truck: initialData.truck?._id || initialData.truck || selectedTruckIdsFromChecklist(initialData)[0] || prev.truck,
            selectedTrucks: selectedTruckIdsFromChecklist(initialData),
            truckMileages: hydrateTruckMileagesFromChecklist(initialData),
            weekStartDate: initialData.weekStartDate ? new Date(initialData.weekStartDate) : prev.weekStartDate,
            weekEndDate: initialData.weekEndDate ? new Date(initialData.weekEndDate) : prev.weekEndDate,
            vehicleInspection: initialData.vehicleInspection ? { ...prev.vehicleInspection, ...initialData.vehicleInspection } : prev.vehicleInspection,
            safetyEquipment: initialData.safetyEquipment ? { ...prev.safetyEquipment, ...initialData.safetyEquipment } : prev.safetyEquipment,
            safetyQuestions: initialData.safetyQuestions ? { ...prev.safetyQuestions, ...initialData.safetyQuestions } : prev.safetyQuestions,
            fuelPurchases: initialData.fuelPurchases ?? prev.fuelPurchases,
            maintenanceIssues: initialData.maintenanceIssues ?? prev.maintenanceIssues,
            additionalNotes: initialData.additionalNotes ?? prev.additionalNotes,
            oversizeLoadedMiles: initialData.oversizeLoadedMiles ?? prev.oversizeLoadedMiles,
            dotInspectionCompleted: initialData.dotInspectionCompleted ?? prev.dotInspectionCompleted,
            hasReimbursement: initialData.hasReimbursement != null
                ? Boolean(initialData.hasReimbursement)
                : (Number(initialData.cashReceiptsAmount) > 0 || Boolean(initialData.cashReceiptsNote)),
            cashReceiptsAmount: initialData.cashReceiptsAmount ?? prev.cashReceiptsAmount,
            cashReceiptsNote: initialData.cashReceiptsNote ?? prev.cashReceiptsNote,
            hasBreakdownPay: Boolean(initialData.hasBreakdownPay),
            breakdownEvents: Array.isArray(initialData.breakdownEvents) ? initialData.breakdownEvents : prev.breakdownEvents,
            detentionEvents: Array.isArray(initialData.detentionEvents) ? initialData.detentionEvents : prev.detentionEvents,
            weekendPay: normalizeWeekendPay(initialData.weekendPay),
            weeklyLoads: Array.isArray(initialData.weeklyLoads) ? initialData.weeklyLoads : prev.weeklyLoads,
            dailyMetrics: initialData.dailyMetrics ? {
                nightsOut: Array.isArray(initialData.dailyMetrics.nightsOut) ? initialData.dailyMetrics.nightsOut : prev.dailyMetrics.nightsOut,
                tarps: Array.isArray(initialData.dailyMetrics.tarps)
                    ? initialData.dailyMetrics.tarps.map(normalizeTarpValue)
                    : prev.dailyMetrics.tarps,
                extraDropPickup: Array.isArray(initialData.dailyMetrics.extraDropPickup) ? initialData.dailyMetrics.extraDropPickup : prev.dailyMetrics.extraDropPickup,
                wwWoodCabinetDrops: Array.isArray(initialData.dailyMetrics.wwWoodCabinetDrops) ? initialData.dailyMetrics.wwWoodCabinetDrops : prev.dailyMetrics.wwWoodCabinetDrops,
                preLoadNotDelivering: Array.isArray(initialData.dailyMetrics.preLoadNotDelivering) ? initialData.dailyMetrics.preLoadNotDelivering : prev.dailyMetrics.preLoadNotDelivering,
                oversizeTruss: Array.isArray(initialData.dailyMetrics.oversizeTruss) ? initialData.dailyMetrics.oversizeTruss : prev.dailyMetrics.oversizeTruss,
                shortRunYes: Array.isArray(initialData.dailyMetrics.shortRunYes)
                    ? initialData.dailyMetrics.shortRunYes
                    : (Array.isArray(initialData.dailyMetrics.shortRunDelivering)
                        ? initialData.dailyMetrics.shortRunDelivering.map(v => (parseInt(v, 10) > 0 ? 'yes' : (v === '0' ? 'no' : '')))
                        : prev.dailyMetrics.shortRunYes),
                shortRunOrders: Array.isArray(initialData.dailyMetrics.shortRunOrders)
                    ? initialData.dailyMetrics.shortRunOrders
                    : Array.from({ length: 7 }, () => [])
            } : prev.dailyMetrics
        }));
        setInfo('Loaded checklist for editing. Submitting will resubmit this week.');
        // Disable auto-save until first change after load
        setAutoSaveEnabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData?._id]);

    const fetchTrucks = async ({ silent = false, includeStale } = {}) => {
        const useIncludeStale = includeStale !== undefined ? includeStale : includeStaleSamsara;
        const isInitialLoad = !trucksLastFetchedAt;
        if (isInitialLoad) {
            setTrucksLoading(true);
        } else {
            setTrucksRefreshing(true);
        }
        // Snapshot existing IDs so we can compute the delta after the merge.
        const prevIds = new Set((trucks || []).map((t) => String(t._id)));
        const logPrefix = '[WeeklyChecklistForm][fetchTrucks]';
        try {
            console.log(`${logPrefix} START`, {
                role: user?.role,
                isInitialLoad,
                includeStale: useIncludeStale,
            });

            // Samsara connection status is admin-only. Company users already get
            // their trucks from /forms/user-trucks, so skip the call that 403s.
            const canQuerySamsara = user?.role === 'admin' || user?.role === 'company_admin';
            let isConnected = false;
            if (canQuerySamsara) {
                isConnected = await samsaraPollingService.checkConnection();
                console.log(`${logPrefix} Samsara connected:`, isConnected);
            }
            setSamsaraConnected(isConnected);

            // Fetch local + Samsara trucks. Default hides units with no trips in 12 months;
            // "Include vehicles without recent trips" overrides that.
            console.log(`${logPrefix} GET /forms/user-trucks`, { onlyWithRecentTrips: !useIncludeStale });
            const response = await apiClient.get('/forms/user-trucks', {
                params: {
                    onlyWithRecentTrips: !useIncludeStale,
                    ...(silent ? {} : { _t: Date.now() }),
                },
            });
            const rawData = response.data?.data;
            let fetchedTrucks = Array.isArray(rawData) ? rawData : [];
            console.log(`${logPrefix} /forms/user-trucks response`, {
                dataArrayLength: fetchedTrucks.length,
                fullResponseKeys: Object.keys(response.data || {}),
                trucksDetail: fetchedTrucks.map((t, i) => ({
                    index: i,
                    _id: t._id,
                    unitNumber: t.unitNumber || t.truckNumber,
                    company: t.company,
                    type: t.type,
                    make: t.make,
                    model: t.model,
                })),
            });

            // Fallback to /trucks if necessary
            if (!Array.isArray(fetchedTrucks) || fetchedTrucks.length === 0) {
                console.log(`${logPrefix} user-trucks empty or not array – fallback GET /trucks`);
                try {
                    const allTrucksRes = await apiClient.get('/trucks');
                    const allTrucks = allTrucksRes.data || [];
                    fetchedTrucks = allTrucks.filter(t => (t.type || '').toLowerCase() !== 'trailer');
                    console.log(`${logPrefix} /trucks fallback`, {
                        totalFromApi: allTrucks.length,
                        afterExcludingTrailers: fetchedTrucks.length,
                        trucksDetail: fetchedTrucks.map((t, i) => ({
                            index: i,
                            _id: t._id,
                            unitNumber: t.unitNumber || t.truckNumber,
                            company: t.company,
                            type: t.type,
                        })),
                    });
                } catch (e) {
                    console.warn(`${logPrefix} /trucks fallback failed`, e?.message || e);
                }
            }

            // If Samsara is connected, fetch and merge Samsara vehicles
            if (isConnected) {
                try {
                    // Fetch Samsara vehicles and assets. When the user has flipped on
                    // "Include vehicles without recent trips", drop the recency filter so
                    // brand-new / parked vehicles flow through.
                    const samsaraParams = useIncludeStale ? {} : { onlyWithRecentTrips: true };
                    const [vehiclesResponse, assetsResponse] = await Promise.all([
                        apiClient.get('/samsara/vehicles', { params: samsaraParams }).catch(() => ({ data: { data: [] } })),
                        apiClient.get('/samsara/vehicles/assets', { params: samsaraParams }).catch(() => ({ data: { data: [] } }))
                    ]);

                    const samsaraVehicles = vehiclesResponse.data.data || [];
                    const samsaraAssets = assetsResponse.data.data || [];

                    // Create assets map
                    const assetsMap = new Map();
                    samsaraAssets.forEach(asset => {
                        const assetId = String(asset.id || asset.assetId || asset.vehicleId || '');
                        if (assetId) {
                            assetsMap.set(assetId, asset);
                        }
                    });

                    // Convert Samsara vehicles to truck-like format
                    const samsaraTrucks = samsaraVehicles
                        .map(vehicle => {
                            const vehicleId = String(vehicle.id || '');
                            const asset = assetsMap.get(vehicleId);
                            const assetData = asset?.vehicle || asset;

                            // Check if this vehicle already exists in local trucks (by VIN, unit number, or Samsara ID)
                            const existingTruck = fetchedTrucks.find(t => 
                                (t.vin && assetData?.vin && t.vin.toUpperCase() === assetData.vin.toUpperCase()) ||
                                (t.unitNumber && vehicle.name && t.unitNumber === vehicle.name) ||
                                (t.samsaraId && t.samsaraId === vehicleId)
                            );

                            // Skip if already in local database
                            if (existingTruck) {
                                return null;
                            }

                            // Create a virtual truck object for Samsara vehicles
                            // Use a special ID format: "samsara-{id}" so we can identify them
                            return {
                                _id: `samsara-${vehicleId}`, // Virtual ID for form compatibility
                                samsaraId: vehicleId,
                                unitNumber: vehicle.name || `Vehicle ${vehicleId}`,
                                vin: assetData?.vin || asset?.vin || assetData?.vehicleVin || asset?.vehicleVin,
                                make: assetData?.make || asset?.make || assetData?.vehicleMake || asset?.vehicleMake,
                                model: assetData?.model || asset?.model || assetData?.vehicleModel || asset?.vehicleModel,
                                year: assetData?.year || asset?.year || assetData?.vehicleYear || asset?.vehicleYear,
                                type: mapSamsaraVehicleType({ ...vehicle, tags: vehicle.tags || asset?.tags || assetData?.tags }),
                                status: 'Active',
                                isSamsaraVehicle: true // Flag to identify Samsara vehicles
                            };
                        })
                        .filter(Boolean); // Remove null entries

                    // Merge local trucks with Samsara trucks
                    const beforeMerge = fetchedTrucks.length;
                    fetchedTrucks = [...fetchedTrucks, ...samsaraTrucks];
                    console.log(`${logPrefix} Samsara merge`, {
                        localCount: beforeMerge,
                        samsaraAdded: samsaraTrucks.length,
                        totalAfterMerge: fetchedTrucks.length,
                        samsaraIds: samsaraTrucks.map(t => t._id),
                    });
                } catch (samsaraError) {
                    console.warn(`${logPrefix} Samsara fetch failed`, samsaraError?.message || samsaraError);
                }
            }

            // Sort by unit number
            fetchedTrucks = excludeRetiredTrucks(fetchedTrucks);
            fetchedTrucks.sort((a, b) => {
                const aNum = a.unitNumber || '';
                const bNum = b.unitNumber || '';
                return aNum.localeCompare(bNum, undefined, { numeric: true, sensitivity: 'base' });
            });

            console.log(`${logPrefix} FINAL truck list`, {
                totalCount: fetchedTrucks.length,
                userRole: user?.role,
                userCompany: user?.company,
                trucks: fetchedTrucks.map((t, i) => ({
                    index: i,
                    _id: t._id,
                    unitNumber: t.unitNumber || t.truckNumber,
                    company: t.company,
                    type: t.type,
                    isSamsara: Boolean(t.isSamsaraVehicle),
                })),
            });
            setTrucks(fetchedTrucks);
            setTrucksLastFetchedAt(new Date());

            // For non-silent (manual refresh / toggle) calls, surface a delta.
            if (!silent && !isInitialLoad) {
                const newOnes = fetchedTrucks.filter((t) => !prevIds.has(String(t._id)));
                if (newOnes.length > 0) {
                    setRefreshNotice({
                        severity: 'success',
                        text: `Found ${newOnes.length} new truck${newOnes.length === 1 ? '' : 's'}`,
                    });
                } else {
                    setRefreshNotice({ severity: 'info', text: 'Truck list is up to date' });
                }
            }

            setFormData((prev) => {
                const selected = Array.isArray(prev.selectedTrucks) ? prev.selectedTrucks : [];
                const miles = Array.isArray(prev.truckMileages) ? prev.truckMileages : [];
                if (selected.length === 0 && miles.length === 0) {
                    if (Array.isArray(fetchedTrucks) && fetchedTrucks.length === 1) {
                        const id = fetchedTrucks[0]._id;
                        return {
                            ...prev,
                            truck: id,
                            selectedTrucks: [id],
                            truckMileages: [{ truckId: id, mileageStart: '', mileageEnd: '' }]
                        };
                    }
                    return prev;
                }
                const mappedSelected = selected.map((id) => canonicalTruckOptionId(id, fetchedTrucks) || id);
                const mappedMiles = miles.map((row) => ({
                    ...row,
                    truckId: canonicalTruckOptionId(row.truckId, fetchedTrucks) || row.truckId
                }));
                return {
                    ...prev,
                    truck: mappedSelected[0] || prev.truck,
                    selectedTrucks: mappedSelected,
                    truckMileages: mappedMiles
                };
            });
        } catch (error) {
            console.error('[WeeklyChecklistForm][fetchTrucks] ERROR', {
                message: error?.message,
                response: error?.response?.data,
                status: error?.response?.status,
                userRole: user?.role,
                userCompany: user?.company,
            }, error);
            setError('Failed to load trucks');
            if (!silent && !isInitialLoad) {
                setRefreshNotice({ severity: 'error', text: 'Failed to refresh trucks' });
            }
        } finally {
            setTrucksLoading(false);
            setTrucksRefreshing(false);
        }
    };

    // Helper: get current week Sunday (start) and Saturday (end) for a given date
    const getCurrentWeekRange = (now = new Date()) => {
        const current = new Date(now);
        current.setHours(0, 0, 0, 0);
        const day = current.getDay(); // 0=Sun ... 6=Sat
        const start = new Date(current);
        start.setDate(current.getDate() - day);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(0, 0, 0, 0);
        return { start, end };
    };

    // Initialize default week range on mount if not already set
    useEffect(() => {
        console.log('[WeeklyChecklistForm] init week range effect', {
            hasWeekStart: Boolean(formData.weekStartDate),
            hasWeekEnd: Boolean(formData.weekEndDate)
        });
        if (!formData.weekStartDate || !formData.weekEndDate) {
            const { start, end } = getCurrentWeekRange(new Date());
            console.log('[WeeklyChecklistForm] setting default week range', { start, end });
            setFormData(prev => ({
                ...prev,
                weekStartDate: prev.weekStartDate || start,
                weekEndDate: prev.weekEndDate || end
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // If start date is set and end date is empty, default end to Saturday of the same week
    useEffect(() => {
        if (formData.weekStartDate && !formData.weekEndDate) {
            console.log('[WeeklyChecklistForm] deriving weekEnd from weekStart', { weekStartDate: formData.weekStartDate });
            const start = new Date(formData.weekStartDate);
            const day = start.getDay();
            const weekStart = new Date(start);
            weekStart.setDate(start.getDate() - day);
            weekStart.setHours(0, 0, 0, 0);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            weekEnd.setHours(0, 0, 0, 0);
            setFormData(prev => ({ ...prev, weekEndDate: weekEnd }));
        }
    }, [formData.weekStartDate]);

    // When the calendar week changes, allow a fresh draft load for that week
    const weekStartKey = toLocalDateKey(formData.weekStartDate);
    const lastWeekKeyRef = React.useRef(weekStartKey);
    useEffect(() => {
        if (!weekStartKey) return;
        if (lastWeekKeyRef.current === weekStartKey) return;
        lastWeekKeyRef.current = weekStartKey;
        setDraftLoaded(false);
        setDraftId(null);
        setAutoSaveEnabled(false);
    }, [weekStartKey]);

    // Load existing draft for the current week, once we have a weekStartDate
    useEffect(() => {
        const loadDraft = async () => {
            // Guard BEFORE try/finally so we don't set draftLoaded prematurely
            if (!formData.weekStartDate) {
                console.log('[WeeklyChecklistForm][DraftLoad] skipped: no weekStartDate yet');
                return;
            }
            if (draftLoaded) {
                console.log('[WeeklyChecklistForm][DraftLoad] skipped: already attempted');
                return;
            }
            try {
                console.log('[WeeklyChecklistForm][DraftLoad] attempting draft load');
                const start = toWeekStartCanonical(formData.weekStartDate);
                if (!start) {
                    setDraftLoaded(true);
                    setAutoSaveEnabled(false);
                    return;
                }
                const qs = encodeURIComponent(start.toISOString());
                const url = `/forms/weekly-checklist/drafts?weekStartDate=${qs}`;
                console.log('[WeeklyChecklistForm][DraftLoad] GET', url);
                const { data } = await apiClient.get(url);
                const draft = data?.data;
                console.log('[WeeklyChecklistForm][DraftLoad] response:', draft);
                if (draft) {
                    setDraftId(draft._id || null);
                    setReceiptAttachments(Array.isArray(draft.cashReceiptsAttachments) ? draft.cashReceiptsAttachments : []);
                    const draftMiles = hydrateTruckMileagesFromDraft(draft).map((row) => ({
                        ...row,
                        truckId: canonicalTruckOptionId(row.truckId, trucks) || row.truckId
                    }));
                    const draftSelected = selectedTrucksFromDraft(draft).map((id) => canonicalTruckOptionId(id, trucks) || id);
                    setFormData(prev => ({
                        ...prev,
                        truck: (draftSelected[0] || truckRefId(draft.truck) || prev.truck),
                        selectedTrucks: draftSelected.length ? draftSelected : prev.selectedTrucks,
                        truckMileages: draftMiles.length ? draftMiles : prev.truckMileages,
                        trailer: truckRefId(draft.trailer) || prev.trailer,
                        selectedTrailers: Array.isArray(draft.selectedTrailers) ? draft.selectedTrailers.map((t) => truckRefId(t)).filter(Boolean) : prev.selectedTrailers,
                        weekStartDate: draft.weekStartDate ? new Date(draft.weekStartDate) : prev.weekStartDate,
                        weekEndDate: draft.weekEndDate ? new Date(draft.weekEndDate) : prev.weekEndDate,
                        vehicleInspection: draft.vehicleInspection ? { ...prev.vehicleInspection, ...draft.vehicleInspection } : prev.vehicleInspection,
                        safetyEquipment: draft.safetyEquipment ? { ...prev.safetyEquipment, ...draft.safetyEquipment } : prev.safetyEquipment,
                        safetyQuestions: draft.safetyQuestions ? { ...prev.safetyQuestions, ...draft.safetyQuestions } : prev.safetyQuestions,
                        fuelPurchases: draft.fuelPurchases ?? prev.fuelPurchases,
                        maintenanceIssues: draft.maintenanceIssues ?? prev.maintenanceIssues,
                        additionalNotes: draft.additionalNotes ?? prev.additionalNotes,
                oversizeLoadedMiles: draft.oversizeLoadedMiles ?? prev.oversizeLoadedMiles,
                dotInspectionCompleted: draft.dotInspectionCompleted ?? prev.dotInspectionCompleted,
                hasReimbursement: draft.hasReimbursement != null
                    ? Boolean(draft.hasReimbursement)
                    : (Number(draft.cashReceiptsAmount) > 0 || Boolean(draft.cashReceiptsNote)),
                cashReceiptsAmount: draft.cashReceiptsAmount ?? prev.cashReceiptsAmount,
                cashReceiptsNote: draft.cashReceiptsNote ?? prev.cashReceiptsNote,
                hasBreakdownPay: Boolean(draft.hasBreakdownPay),
                breakdownEvents: Array.isArray(draft.breakdownEvents) ? draft.breakdownEvents : prev.breakdownEvents,
                detentionEvents: Array.isArray(draft.detentionEvents) ? draft.detentionEvents : prev.detentionEvents,
                weekendPay: normalizeWeekendPay(draft.weekendPay),
                weeklyLoads: Array.isArray(draft.weeklyLoads) ? draft.weeklyLoads : prev.weeklyLoads,
                        dailyMetrics: draft.dailyMetrics ? {
                            nightsOut: Array.isArray(draft.dailyMetrics.nightsOut) ? draft.dailyMetrics.nightsOut : prev.dailyMetrics.nightsOut,
                            tarps: Array.isArray(draft.dailyMetrics.tarps)
                                ? draft.dailyMetrics.tarps.map(normalizeTarpValue)
                                : prev.dailyMetrics.tarps,
                            extraDropPickup: Array.isArray(draft.dailyMetrics.extraDropPickup) ? draft.dailyMetrics.extraDropPickup : prev.dailyMetrics.extraDropPickup,
                            wwWoodCabinetDrops: Array.isArray(draft.dailyMetrics.wwWoodCabinetDrops) ? draft.dailyMetrics.wwWoodCabinetDrops : prev.dailyMetrics.wwWoodCabinetDrops,
                            preLoadNotDelivering: Array.isArray(draft.dailyMetrics.preLoadNotDelivering) ? draft.dailyMetrics.preLoadNotDelivering : prev.dailyMetrics.preLoadNotDelivering,
                            oversizeTruss: Array.isArray(draft.dailyMetrics.oversizeTruss) ? draft.dailyMetrics.oversizeTruss : prev.dailyMetrics.oversizeTruss,
                            shortRunYes: Array.isArray(draft.dailyMetrics.shortRunYes)
                                ? draft.dailyMetrics.shortRunYes
                                : (Array.isArray(draft.dailyMetrics.shortRunDelivering)
                                    ? draft.dailyMetrics.shortRunDelivering.map(v => (parseInt(v, 10) > 0 ? 'yes' : (v === '0' ? 'no' : '')))
                                    : prev.dailyMetrics.shortRunYes),
                            shortRunOrders: Array.isArray(draft.dailyMetrics.shortRunOrders)
                                ? draft.dailyMetrics.shortRunOrders
                                : Array.from({ length: 7 }, () => [])
                        } : prev.dailyMetrics
                    }));
                    setInfo('Draft restored for this week.');
                    console.log('[WeeklyChecklistForm][DraftLoad] draft restored');
                    // Auto-open the form when draft is present
                    setFormOpen(true);
                }
            } catch (e) {
                console.warn('[WeeklyChecklistForm][DraftLoad] no draft or error:', e?.response?.data || e?.message);
            } finally {
                setDraftLoaded(true);
                // Wait for a real user edit before autosaving (avoids empty upsert wiping drafts)
                setAutoSaveEnabled(false);
                console.log('[WeeklyChecklistForm][DraftLoad] finished; draftLoaded=true');
            }
        };
        loadDraft();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.weekStartDate, draftLoaded]);

    const inspectionOptions = [
        { value: 'pass', label: 'Pass', color: 'success' },
        { value: 'fail', label: 'Fail', color: 'error' },
        { value: 'needs_attention', label: 'Needs Attention', color: 'warning' }
    ];

    const safetyEquipmentOptions = {
        fireExtinguisher: [
            { value: 'present', label: 'Present' },
            { value: 'missing', label: 'Missing' },
            { value: 'expired', label: 'Expired' }
        ],
        firstAidKit: [
            { value: 'present', label: 'Present' },
            { value: 'missing', label: 'Missing' },
            { value: 'incomplete', label: 'Incomplete' }
        ],
        emergencyTriangles: [
            { value: 'present', label: 'Present' },
            { value: 'missing', label: 'Missing' },
            { value: 'damaged', label: 'Damaged' }
        ],
        tieDownEquipment: [
            { value: 'sufficient', label: 'Sufficient' },
            { value: 'insufficient', label: 'Insufficient' },
            { value: 'damaged', label: 'Damaged' }
        ]
    };

    const handleInputChange = (field, value) => {
        if (!autoSaveEnabled) setAutoSaveEnabled(true);
        // If multi-select trucks changes, also set primary truck
        if (field === 'selectedTrucks') {
            const selected = Array.isArray(value) ? value : [];
            const primaryTruck = selected[0] || '';
            setFormData(prev => ({
                ...prev,
                selectedTrucks: selected,
                truck: primaryTruck,
                truckMileages: truckMileagesForSelection(selected, prev.truckMileages || [])
            }));
            if (error) setError('');
            return;
        }
        if (field === 'hasBreakdownPay') {
            setFormData(prev => ({
                ...prev,
                hasBreakdownPay: value,
                breakdownEvents: value && (!prev.breakdownEvents || prev.breakdownEvents.length === 0)
                    ? [{ date: null, notificationTime: '' }]
                    : prev.breakdownEvents
            }));
            if (error) setError('');
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        if (error) setError('');
    };

    const handleNestedInputChange = (section, field, value) => {
        if (!autoSaveEnabled) setAutoSaveEnabled(true);
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
        
        if (error) setError('');
    };

    const validateForm = () => {
        const hasPrimaryTruck = Boolean(formData.truck);
        const hasSelectedTrucks = Array.isArray(formData.selectedTrucks) && formData.selectedTrucks.length > 0;
        if (!hasPrimaryTruck && !hasSelectedTrucks) {
            setError('Please select a truck');
            return false;
        }
        
        if (!formData.weekStartDate) {
            setError('Week start date is required');
            return false;
        }
        
        if (!formData.weekEndDate) {
            setError('Week end date is required');
            return false;
        }
        
        if (formData.weekEndDate <= formData.weekStartDate) {
            setError('Week end date must be after start date');
            return false;
        }
        
        const mileRows = Array.isArray(formData.truckMileages) ? formData.truckMileages : [];
        if (mileRows.length === 0) {
            setError('Please select a truck and enter odometer readings');
            return false;
        }
        for (let i = 0; i < mileRows.length; i++) {
            const row = mileRows[i];
            const start = parseOdometerInput(row.mileageStart);
            const end = parseOdometerInput(row.mileageEnd);
            if (!Number.isFinite(start) || !Number.isFinite(end)) {
                setError(`Both starting and ending mileage are required for truck ${i + 1}`);
                return false;
            }
            if (end <= start) {
                setError(`Ending mileage must be greater than starting mileage for truck ${i + 1}`);
                return false;
            }
        }
        
        // Tarps and Short Run default to "-" (empty) and are optional.
        // Only validate Short Run order #s when a day is explicitly marked Yes.
        if (isAll || isFlatbeds) {
            setTarpsErrors(Array(7).fill(false));
            setShortRunYesErrors(Array(7).fill(false));

            const yesArr = formData?.dailyMetrics?.shortRunYes || [];
            const orders = formData?.dailyMetrics?.shortRunOrders || [];
            const srOrderErrs = Array(7).fill(false);
            for (let d = 0; d < 7; d++) {
                if (yesArr[d] === 'yes') {
                    const dayOrders = Array.isArray(orders[d]) ? orders[d] : [];
                    const hasValid = dayOrders.some(o => /\d+/.test(String(o || '').trim()));
                    if (!hasValid) srOrderErrs[d] = true;
                }
            }
            setShortRunOrderErrors(srOrderErrs);
            const firstOrderIdx = srOrderErrs.findIndex(Boolean);
            if (firstOrderIdx !== -1) {
                setError(`Please add at least one order # for Short Run on ${dayLabels[firstOrderIdx]}.`);
                if (isMobile) setMobileDay(firstOrderIdx);
                setTimeout(() => scrollToMetric(`metric-shortRun-orders-day-${firstOrderIdx}`), 50);
                return false;
            }
        }

        // Require at least one weekly load entry and validate fields
        if (!Array.isArray(formData.weeklyLoads) || formData.weeklyLoads.length === 0) {
            setHighlightWeeklyLoads(true);
            setWeeklyLoadsErrors([]);
            setError('Please add at least one Weekly Load (date, ELD order #, pickup, and delivery).');
            return false;
        } else {
            const errs = formData.weeklyLoads.map((row) => ({
                date: !row?.date,
                eldOrderNumber: !String(row?.eldOrderNumber || '').trim(),
                from: !row?.from,
                to: !row?.to
            }));
            const hasAnyError = errs.some(e => e.date || e.eldOrderNumber || e.from || e.to);
            setWeeklyLoadsErrors(errs);
            setHighlightWeeklyLoads(hasAnyError);
            if (hasAnyError) {
                setError('Please complete all Weekly Loads fields (date, ELD order #, pickup city/state, and delivery city/state).');
                return false;
            }
        }

        if (formData.hasReimbursement) {
            const amt = parseFloat(String(formData.cashReceiptsAmount).replace(/,/g, '').trim());
            if (!Number.isFinite(amt) || amt <= 0) {
                setError('Enter a reimbursement amount greater than 0.');
                return false;
            }
            if (!Array.isArray(receiptAttachments) || receiptAttachments.length < 1) {
                setError('Upload at least one receipt photo for reimbursement.');
                return false;
            }
        }

        if (formData.hasBreakdownPay) {
            const rows = (formData.breakdownEvents || []).filter((ev) => !breakdownRowIsEmpty(ev));
            if (rows.length < 1) {
                setError('Add at least one breakdown with Time of Notification.');
                return false;
            }
            if (rows.some((ev) => !breakdownRowIsComplete(ev))) {
                setError('Each breakdown needs a date and Time of Notification.');
                return false;
            }
        }
        
        return true;
    };

    const calculateMilesDriven = () => {
        const rows = Array.isArray(formData.truckMileages) ? formData.truckMileages : [];
        return rows.reduce((sum, row) => {
            const s = parseOdometerInput(row.mileageStart);
            const e = parseOdometerInput(row.mileageEnd);
            if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return sum;
            return sum + (e - s);
        }, 0);
    };

    const updateTruckMileageRow = (index, field, value) => {
        if (!autoSaveEnabled) setAutoSaveEnabled(true);
        setFormData((prev) => {
            const next = [...(prev.truckMileages || [])];
            if (!next[index]) return prev;
            next[index] = { ...next[index], [field]: value };
            return { ...prev, truckMileages: next };
        });
        if (error) setError('');
    };

    const buildDraftPayload = (data) => {
        const payload = {
            ...data,
            weekStartDate: toWeekStartCanonical(data.weekStartDate) || data.weekStartDate,
            truck: data.truck || undefined,
            selectedTrucks: Array.isArray(data.selectedTrucks) ? data.selectedTrucks : [],
            fuelPurchases: data.fuelPurchases ? Number(data.fuelPurchases) : 0,
            oversizeLoadedMiles: data.oversizeLoadedMiles ? Number(data.oversizeLoadedMiles) : 0,
            cashReceiptsAmount: data.cashReceiptsAmount ? Number(data.cashReceiptsAmount) : 0
        };
        delete payload.cashReceiptsAttachments;
        return payload;
    };

    const ensureDraftId = async () => {
        if (draftIdRef.current) return draftIdRef.current;
        const { data } = await apiClient.post('/forms/weekly-checklist/drafts', buildDraftPayload(formData));
        const id = data?.data?._id || null;
        if (id) {
            draftIdRef.current = id;
            setDraftId(id);
        }
        return id;
    };

    const handleReceiptFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        if (!autoSaveEnabled) setAutoSaveEnabled(true);
        setReceiptError('');
        const remaining = 10 - receiptAttachments.length;
        if (remaining <= 0) {
            setReceiptError('You can attach at most 10 receipt photos');
            return;
        }
        const toUpload = files.slice(0, remaining);
        setReceiptUploading(true);
        try {
            const id = await ensureDraftId();
            if (!id) throw new Error('Could not save a draft before uploading');
            for (const file of toUpload) {
                if (file.size > 10 * 1024 * 1024) {
                    setReceiptError('Each receipt must be 10MB or smaller');
                    continue;
                }
                const urlRes = await apiClient.post(`/forms/weekly-checklist/drafts/${id}/receipts/upload-url`, {
                    filename: file.name,
                    contentType: file.type,
                    size: file.size,
                });
                const { uploadUrl, key, bucket, contentType } = urlRes.data.data;
                const putRes = await fetch(uploadUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': contentType },
                    body: file,
                });
                if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
                const done = await apiClient.post(`/forms/weekly-checklist/drafts/${id}/receipts/complete`, {
                    key,
                    bucket,
                    contentType,
                    originalFilename: file.name,
                    size: file.size,
                });
                const atts = done.data?.data?.cashReceiptsAttachments;
                if (Array.isArray(atts)) setReceiptAttachments(atts);
            }
        } catch (err) {
            setReceiptError(err.response?.data?.message || err.message || 'Failed to upload receipt');
        } finally {
            setReceiptUploading(false);
            if (receiptInputRef.current) receiptInputRef.current.value = '';
        }
    };

    const handleRemoveReceipt = async (att) => {
        const id = draftIdRef.current;
        const attId = att?._id || att?.id;
        if (!id || !attId) return;
        setReceiptRemovingId(String(attId));
        setReceiptError('');
        try {
            const { data } = await apiClient.delete(`/forms/weekly-checklist/drafts/${id}/receipts/${attId}`);
            const atts = data?.data?.cashReceiptsAttachments;
            setReceiptAttachments(Array.isArray(atts) ? atts : []);
        } catch (err) {
            setReceiptError(err.response?.data?.message || err.message || 'Failed to remove receipt');
        } finally {
            setReceiptRemovingId(null);
        }
    };

    const getTruckLabelById = (truckId) => {
        const t = trucks.find((x) => truckOptionMatchesRef(x, truckId));
        if (!t) return truckId ? String(truckId) : 'Truck';
        const unit = t.unitNumber || t.truckNumber || '';
        const mm = [t.make, t.model].filter(Boolean).join(' ');
        return [unit, mm].filter(Boolean).join(' — ') || `Vehicle ${truckId}`;
    };

    const getOverallStatus = () => {
        const vehicleIssues = Object.values(formData.vehicleInspection).some(status => status === 'fail');
        const safetyIssues = Object.values(formData.safetyEquipment).some(status => 
            status === 'missing' || status === 'expired' || status === 'damaged' || status === 'insufficient'
        );
        const complianceIssues = Object.values(formData.safetyQuestions).some(answer => !answer);
        
        if (vehicleIssues || safetyIssues || complianceIssues) {
            return vehicleIssues ? 'critical' : 'needs_attention';
        }
        
        return 'satisfactory';
    };

    const resetForm = () => {
        setFormData({
            truck: '',
            selectedTrucks: [],
            weekStartDate: null,
            weekEndDate: null,
            vehicleInspection: {
                brakes: 'pass',
                tires: 'pass',
                lights: 'pass',
                engine: 'pass',
                transmission: 'pass',
                steering: 'pass',
                mirrors: 'pass',
                windshield: 'pass'
            },
            safetyEquipment: {
                fireExtinguisher: 'present',
                firstAidKit: 'present',
                emergencyTriangles: 'present',
                tieDownEquipment: 'sufficient'
            },
            safetyQuestions: {
                hoursOfServiceCompliance: true,
                drugAlcoholPolicy: true,
                safetyTrainingCurrent: true,
                incidentsReported: true
            },
            truckMileages: [],
            fuelPurchases: '',
            maintenanceIssues: '',
            additionalNotes: '',
            oversizeLoadedMiles: '',
            dotInspectionCompleted: false,
            hasReimbursement: false,
            cashReceiptsAmount: '',
            cashReceiptsNote: '',
            hasBreakdownPay: false,
            breakdownEvents: [],
            detentionEvents: [],
            weekendPay: 'none',
            weeklyLoads: [],
            dailyMetrics: {
                nightsOut: Array(7).fill(false),
                tarps: Array(7).fill(''),
                extraDropPickup: Array(7).fill(''),
                wwWoodCabinetDrops: Array(7).fill(''),
                preLoadNotDelivering: Array(7).fill(''),
                oversizeTruss: Array(7).fill(false),
                shortRunYes: Array(7).fill(''),
                shortRunOrders: Array.from({ length: 7 }, () => [])
            }
        });
        setReceiptAttachments([]);
        setReceiptError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setLoading(true);
        setError('');
        setSuccess('');
        
        try {
            const prevTruck = formData.truck;

            const prevWeekStart = formData.weekStartDate;
            const prevWeekEnd = formData.weekEndDate;

            const tmPayload = (formData.truckMileages || []).map((row) => {
                const selectedOpt = (trucks || []).find((t) => String(t._id) === String(row.truckId));
                return {
                    truck: row.truckId,
                    mileageStart: parseOdometerInput(row.mileageStart),
                    mileageEnd: parseOdometerInput(row.mileageEnd),
                    unitNumber: selectedOpt?.unitNumber || selectedOpt?.truckNumber || undefined,
                    vin: selectedOpt?.vin || undefined,
                    make: selectedOpt?.make || undefined,
                    model: selectedOpt?.model || undefined,
                    year: selectedOpt?.year || undefined,
                };
            });
            const firstM = tmPayload[0];

            const submitData = {
                ...formData,
                truckMileages: tmPayload,
                truck: formData.truck || (Array.isArray(formData.selectedTrucks) ? formData.selectedTrucks[0] : ''),
                additionalTrucks: Array.isArray(formData.selectedTrucks)
                    ? formData.selectedTrucks.slice(1)
                    : [],
                mileageStart: firstM.mileageStart,
                mileageEnd: firstM.mileageEnd,
                fuelPurchases: parseFloat(formData.fuelPurchases) || 0,
                oversizeLoadedMiles: formData.oversizeLoadedMiles ? Number(formData.oversizeLoadedMiles) : 0,
                cashReceiptsAmount: formData.cashReceiptsAmount ? Number(formData.cashReceiptsAmount) : 0,
                overallStatus: getOverallStatus()
            };
            delete submitData.selectedTrucks;
            let response;
            if (editId) {
                console.log('[WeeklyChecklistForm][Submit][Edit] PATCH with resubmit', editId, submitData);
                response = await apiClient.patch(`/forms/weekly-checklist/${editId}/edit`, { ...submitData, resubmit: true });
            } else {
                console.log('[WeeklyChecklistForm][Submit] submitting payload:', submitData);
                response = await apiClient.post('/forms/weekly-checklist', submitData);
            }
            console.log('[WeeklyChecklistForm][Submit] success:', response.data);
            
            setSuccess(
                editId
                    ? 'Weekly checklist updated and resubmitted. Billing was notified that this is an edited version; it is pending review again.'
                    : 'Weekly checklist submitted successfully! Your checklist has been saved and can be reviewed below.'
            );

            // Delete draft if it exists
            if (draftId) {
                try {
                    console.log('[WeeklyChecklistForm][Submit] deleting draft', draftId);
                    await apiClient.delete(`/forms/weekly-checklist/drafts/${draftId}`);
                    setDraftId(null);
                    console.log('[WeeklyChecklistForm][Submit] draft deleted');
                } catch (delErr) {
                    console.warn('[WeeklyChecklistForm][Submit] failed to delete draft:', delErr?.response?.data || delErr?.message);
                }
            }
            
            // Reset form
            resetForm();
            // Preselect commonly reused values after reset (truck, week range)
            const fallbackRange = getCurrentWeekRange(new Date());
            setFormData(prev => ({
                ...prev,
                truck: prevTruck || prev.truck,
                weekStartDate: prevWeekStart || prev.weekStartDate || fallbackRange.start,
                weekEndDate: prevWeekEnd || prev.weekEndDate || fallbackRange.end,
                selectedTrucks: prevTruck ? [prevTruck] : (Array.isArray(prev.selectedTrucks) ? prev.selectedTrucks : []),
                truckMileages: prevTruck
                    ? [{ truckId: prevTruck, mileageStart: '', mileageEnd: '' }]
                    : (Array.isArray(prev.selectedTrucks) && prev.selectedTrucks.length
                        ? truckMileagesForSelection(prev.selectedTrucks, [])
                        : [])
            }));
            console.log('[WeeklyChecklistForm][Submit] form reset; preselected truck and week reapplied');
            // Clear auto-save indicator and disable until next input
            setAutoSaveState('idle');
            setLastAutoSaveAt(null);
            setAutoSaveEnabled(false);
            setEditId(null);
            
            // Notify parent component to refresh data
            if (onSubmitSuccess) {
                console.log('[WeeklyChecklistForm] Calling onSubmitSuccess callback');
                onSubmitSuccess(response.data.data);
            }
            
            // Close form after a delay and clear success message
            setTimeout(() => {
                setFormOpen(false);
                setSuccess('');
            }, 3000);
            
        } catch (error) {
            console.error('[WeeklyChecklistForm][Submit] error:', error);
            const errs = error.response?.data?.errors;
            const joined = Array.isArray(errs) && errs.length ? errs.join(' ') : null;
            const errorMessage = joined ||
                               error.response?.data?.error ||
                               error.response?.data?.message ||
                               'Failed to submit checklist. Please try again.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Auto-save draft on changes (debounced)
    useEffect(() => {
        if (!draftLoaded) return;
        if (!formData.weekStartDate) return;
        if (!autoSaveEnabled) {
            console.log('[WeeklyChecklistForm][AutoSave] disabled; skipping');
            return;
        }
        console.log('[WeeklyChecklistForm][AutoSave] change detected; scheduling save');
        const timeout = setTimeout(async () => {
            try {
                setAutoSaveState('saving');
                const payload = {
                    ...formData,
                    weekStartDate: toWeekStartCanonical(formData.weekStartDate) || formData.weekStartDate,
                    truck: formData.truck || undefined,
                    
                    selectedTrucks: Array.isArray(formData.selectedTrucks) ? formData.selectedTrucks : [],
                    
                    fuelPurchases: formData.fuelPurchases ? Number(formData.fuelPurchases) : 0,
                    oversizeLoadedMiles: formData.oversizeLoadedMiles ? Number(formData.oversizeLoadedMiles) : 0,
                    cashReceiptsAmount: formData.cashReceiptsAmount ? Number(formData.cashReceiptsAmount) : 0
                };
                delete payload.cashReceiptsAttachments;
                console.log('[WeeklyChecklistForm][AutoSave] dailyMetrics snapshot:', formData.dailyMetrics);
                console.log('[WeeklyChecklistForm][AutoSave] POST /forms/weekly-checklist/drafts payload:', payload);
                const { data } = await apiClient.post('/forms/weekly-checklist/drafts', payload);
                const newId = data?.data?._id || null;
                setDraftId(newId);
                setAutoSaveState('saved');
                const ts = new Date();
                setLastAutoSaveAt(ts);
                console.log('[WeeklyChecklistForm][AutoSave] success; draftId:', newId, 'at', ts.toLocaleString());
            } catch (err) {
                setAutoSaveState('error');
                console.error('[WeeklyChecklistForm][AutoSave] failed:', err?.response?.data || err?.message);
            }
        }, 800);
        return () => {
            clearTimeout(timeout);
            console.log('[WeeklyChecklistForm][AutoSave] debounce cleared');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftLoaded, autoSaveEnabled, JSON.stringify(formData)]);

    const overallStatus = getOverallStatus();
    const statusColor = overallStatus === 'satisfactory' ? 'success' : 
                       overallStatus === 'needs_attention' ? 'warning' : 'error';

    return (
        <Paper sx={{ p: compact ? 1.5 : 3, mb: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={compact ? 1 : 2} sx={compact ? { flexWrap: 'wrap', rowGap: 0.5 } : undefined}>
                <Box sx={compact ? { borderLeft: '4px solid #E94F37', pl: 1.25 } : undefined}>
                    <Typography
                        variant={compact ? 'h5' : 'h6'}
                        sx={compact ? { fontWeight: 800, color: '#E94F37', lineHeight: 1.15 } : undefined}
                    >
                        Submit Weekly Checklist
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    {autoSaveState === 'saving' && (
                        <>
                            <CircularProgress size={16} />
                            <Typography variant="caption" color="text.secondary">Saving…</Typography>
                        </>
                    )}
                    {autoSaveState === 'saved' && (
                        <>
                            <CheckCircle color="success" fontSize="small" />
                            <Typography variant="caption" color="text.secondary">
                                Saved{lastAutoSaveAt ? ` ${new Date(lastAutoSaveAt).toLocaleTimeString()}` : ''}
                            </Typography>
                        </>
                    )}
                    {autoSaveState === 'error' && (
                        <>
                            <ErrorOutline color="error" fontSize="small" />
                            <Typography variant="caption" color="error">Auto-save failed</Typography>
                        </>
                    )}
                </Stack>
                {!compact && (
                <IconButton 
                    onClick={() => setFormOpen(!formOpen)}
                >
                    {formOpen ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                )}
            </Stack>
            
            <Collapse in={formOpen}>
                <Paper 
                    elevation={compact ? 0 : 1}
                    sx={{ 
                        p: compact ? 0 : 3, 
                        mt: compact ? 0 : 2, 
                        backgroundColor: compact ? 'transparent' : 'white',
                        boxShadow: compact ? 'none' : undefined,
                        borderRadius: 2,
                        '& .MuiTypography-root': { color: 'text.primary' }
                    }}
                >
                    <Box component="form" onSubmit={handleSubmit}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        
                        {info && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {info}
                            </Alert>
                        )}

                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {success}
                            </Alert>
                        )}

                        <Snackbar
                            open={Boolean(refreshNotice)}
                            autoHideDuration={3000}
                            onClose={() => setRefreshNotice(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        >
                            {refreshNotice ? (
                                <Alert
                                    onClose={() => setRefreshNotice(null)}
                                    severity={refreshNotice.severity || 'info'}
                                    variant="filled"
                                    sx={{ width: '100%' }}
                                >
                                    {refreshNotice.text}
                                </Alert>
                            ) : undefined}
                        </Snackbar>

                    {/* Basic Information */}
                    <Typography variant="h6" gutterBottom sx={{ mt: compact ? 0.5 : 2 }}>
                        Basic Information
                    </Typography>
                    
                    <Grid container spacing={3}>
                        <Grid item xs={12} sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 1,
                                    bgcolor: 'grey.50',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    flexWrap: 'nowrap',
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    {trucksLoading
                                        ? 'Loading…'
                                        : trucksLastFetchedAt
                                            ? `Last updated ${formatRelativeTime(trucksLastFetchedAt)}`
                                            : 'Not loaded yet'}
                                </Typography>
                                <Tooltip title="Refresh truck list from Samsara">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => fetchTrucks({ silent: false })}
                                            disabled={trucksLoading || trucksRefreshing}
                                            aria-label="Refresh truck list"
                                        >
                                            {trucksRefreshing
                                                ? <CircularProgress size={14} />
                                                : <RefreshIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <FormControlLabel
                                    sx={{ ml: 'auto', mr: 0, flexShrink: 0 }}
                                    control={
                                        <Switch
                                            size="small"
                                            checked={includeStaleSamsara}
                                            onChange={(e) => {
                                                const next = e.target.checked;
                                                setIncludeStaleSamsara(next);
                                                fetchTrucks({ silent: false, includeStale: next });
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                            Include vehicles without GPS activity in the last 12 months
                                        </Typography>
                                    }
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ mb: 0.5, flexWrap: 'wrap', display: { xs: 'flex', md: 'none' } }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    {trucksLoading
                                        ? 'Loading…'
                                        : trucksLastFetchedAt
                                            ? `Last updated ${formatRelativeTime(trucksLastFetchedAt)}`
                                            : 'Not loaded yet'}
                                </Typography>
                                <Tooltip title="Refresh truck list from Samsara">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => fetchTrucks({ silent: false })}
                                            disabled={trucksLoading || trucksRefreshing}
                                            aria-label="Refresh truck list"
                                        >
                                            {trucksRefreshing
                                                ? <CircularProgress size={14} />
                                                : <RefreshIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <FormControlLabel
                                    sx={{ ml: 'auto', mr: 0 }}
                                    control={
                                        <Switch
                                            size="small"
                                            checked={includeStaleSamsara}
                                            onChange={(e) => {
                                                const next = e.target.checked;
                                                setIncludeStaleSamsara(next);
                                                fetchTrucks({ silent: false, includeStale: next });
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="caption">
                                            Include vehicles without GPS activity in the last 12 months
                                        </Typography>
                                    }
                                />
                            </Stack>
                            <Autocomplete
                                multiple
                                options={trucks}
                                loading={trucksLoading}
                                loadingText="Loading trucks…"
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                        const t = trucks.find((x) => truckOptionMatchesRef(x, option));
                                        return t ? (t.unitNumber || t.truckNumber) : option;
                                    }
                                    return option.unitNumber || option.truckNumber || `Vehicle ${option._id}`;
                                }}
                                isOptionEqualToValue={(option, value) => String(option?._id) === String(value?._id)}
                                value={trucks.filter((t) => formData.selectedTrucks.some((id) => truckOptionMatchesRef(t, id)))}
                                onChange={(event, newValue) => {
                                    const selectedIds = newValue.map(t => t._id);
                                    handleInputChange('selectedTrucks', selectedIds);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Truck"
                                        required={formData.selectedTrucks.length === 0}
                                        helperText="Select multiple trucks if you drove more than one this week; enter odometer for each."
                                        placeholder={trucksLoading ? 'Loading…' : (trucks.length > 50
                                            ? `Search by unit number (${trucks.length} trucks)`
                                            : 'Search by unit number…')}
                                    />
                                )}
                                ListboxProps={trucks.length > 50 ? { sx: { maxHeight: 320 } } : undefined}
                                renderOption={(props, option) => {
                                    const unit = option.unitNumber || option.truckNumber;
                                    const isSamsara = option.isSamsaraVehicle || option._id?.startsWith('samsara-');
                                    const isSelected = formData.selectedTrucks.some((id) => truckOptionMatchesRef(option, id));
                                    return (
                                        <li {...props} key={option._id}>
                                            <Checkbox checked={isSelected} sx={{ mr: 1 }} />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontWeight: isSelected ? 600 : 400 }}>{unit}</Typography>
                                                {option.make && option.model && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {[option.year, option.make, option.model].filter(Boolean).join(' ')}
                                                    </Typography>
                                                )}
                                                {isSamsara && (
                                                    <Chip 
                                                        label="Samsara" 
                                                        size="small" 
                                                        color="primary" 
                                                        sx={{ height: 18, fontSize: '0.65rem' }} 
                                                    />
                                                )}
                                            </Box>
                                        </li>
                                    );
                                }}
                                renderTags={(value, getTagProps) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                        {value.map((option, index) => {
                                            const { key, ...tagProps } = getTagProps({ index });
                                            const unit = option.unitNumber || option.truckNumber;
                                            return (
                                                <Chip
                                                    key={key}
                                                    label={unit}
                                                    size="small"
                                                    {...tagProps}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                                filterOptions={(options, { inputValue }) => filterTruckOptions(options, inputValue)}
                                isOptionEqualToValue={(option, value) => String(option._id) === String(value._id)}
                                sx={{ width: '100%' }}
                                disableCloseOnSelect
                                noOptionsText="No trucks found"
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <DatePicker
                                label="Week Start Date"
                                value={formData.weekStartDate}
                                onChange={(newValue) => handleInputChange('weekStartDate', newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        required: true,
                                        helperText: 'Start of week (Sunday)'
                                    }
                                }}
                                format="MM/dd/yyyy"
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <DatePicker
                                label="Week End Date"
                                value={formData.weekEndDate}
                                onChange={(newValue) => handleInputChange('weekEndDate', newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        required: true,
                                        helperText: 'End of week (Saturday)'
                                    }
                                }}
                                minDate={formData.weekStartDate}
                                format="MM/dd/yyyy"
                            />
                        </Grid>
                        

                        {/* Per-truck odometer + total */}
                        {(formData.truckMileages || []).map((row, idx) => (
                            <React.Fragment key={`odo-${String(row.truckId)}-${idx}`}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: idx === 0 ? 0 : 1 }}>
                                        {getTruckLabelById(row.truckId)}
                                        {formData.truckMileages.length > 1 ? ` (truck ${idx + 1})` : ''}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="number"
                                        label="Starting Odometer"
                                        value={row.mileageStart ?? ''}
                                        onChange={(e) => updateTruckMileageRow(idx, 'mileageStart', e.target.value)}
                                        inputProps={{ min: 0, step: 0.1 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="number"
                                        label="Ending Odometer"
                                        value={row.mileageEnd ?? ''}
                                        onChange={(e) => updateTruckMileageRow(idx, 'mileageEnd', e.target.value)}
                                        inputProps={{ min: 0, step: 0.1 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="This truck (mi)"
                                        value={(() => {
                                            const s = parseOdometerInput(row.mileageStart);
                                            const e = parseOdometerInput(row.mileageEnd);
                                            if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return '';
                                            const d = e - s;
                                            return Number.isInteger(d) ? String(d) : d.toFixed(1);
                                        })()}
                                        InputProps={{ readOnly: true }}
                                        sx={{ '& .MuiInputBase-input': { color: 'text.secondary', fontWeight: 600 } }}
                                    />
                                </Grid>
                            </React.Fragment>
                        ))}
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Total mileage (all trucks)"
                                value={(() => {
                                    const miles = calculateMilesDriven();
                                    if (!Number.isFinite(miles) || miles <= 0) return '';
                                    return Number.isInteger(miles) ? miles : miles.toFixed(1);
                                })()}
                                InputProps={{ readOnly: true, sx: { textAlign: 'center' } }}
                                sx={{ '& .MuiInputBase-input': { textAlign: 'center', color: 'text.secondary', fontWeight: 700 } }}
                            />
                        </Grid>
                    </Grid>

                    

                    {/* Weekly Metrics Table */}
                    <Card sx={{ mt: 3 }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                                    Weekly Metrics
                                </Typography>
                                <Typography variant="body2" color="error" fontWeight={700} sx={{ color: (theme) => `${theme.palette.error.main} !important` }}>
                                    * Entries in the table below must be recorded accurately on this checklist. Unrecorded items will not be reflected on paychecks.
                                </Typography>
                            </Stack>
                            {isMobile ? (
                                <Box>
                                    <Tabs
                                        value={mobileDay}
                                        onChange={(e, v) => setMobileDay(v)}
                                        variant="scrollable"
                                        allowScrollButtonsMobile
                                        scrollButtons="auto"
                                        sx={{ mb: 1 }}
                                    >
                                        {dayLabels.map((d, i) => (
                                            <Tab key={d} value={i} label={d} />
                                        ))}
                                    </Tabs>
                                    {(() => {
                                        const controlWidth = 140;
                                        const metricRowSx = {
                                            display: 'grid',
                                            gridTemplateColumns: isTightLayout
                                                ? '1fr'
                                                : `minmax(0, 1fr) minmax(96px, ${controlWidth}px)`,
                                            alignItems: isTightLayout ? 'stretch' : 'center',
                                            py: 0.5,
                                            columnGap: 2,
                                            rowGap: isTightLayout ? 0.5 : 0,
                                            width: '100%',
                                            minWidth: 0,
                                        };
                                        const controlSx = {
                                            justifySelf: isTightLayout ? 'stretch' : 'end',
                                            width: isTightLayout ? '100%' : controlWidth,
                                            maxWidth: '100%',
                                        };
                                        return (
                                            <>
                                                {(isAll || isVans || isFlatbeds) && (
                                                    <Box sx={metricRowSx}>
                                                        <Box>
                                                            <Typography variant="body2">Nights Out</Typography>
                                                        </Box>
                                                        <Box sx={{ justifySelf: isTightLayout ? 'start' : 'end' }}>
                                                            <Checkbox
                                                                checked={Boolean(formData.dailyMetrics.nightsOut[mobileDay])}
                                                                onChange={(e) => {
                                                                    const next = [...formData.dailyMetrics.nightsOut];
                                                                    next[mobileDay] = e.target.checked;
                                                                    handleNestedInputChange('dailyMetrics', 'nightsOut', next);
                                                                }}
                                                                size="small"
                                                                sx={{ p: 0 }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )}

                                                {(isAll || isFlatbeds) && (
                                                    <Box id={`metric-tarps-day-${mobileDay}`} sx={metricRowSx}>
                                                        <Box>
                                                            <Typography variant="body2">Tarps</Typography>
                                                        </Box>
                                                        <FormControl size="small" error={Boolean(tarpsErrors[mobileDay])} sx={controlSx}>
                                                            <Select
                                                                value={formData.dailyMetrics.tarps[mobileDay] ?? ''}
                                                                displayEmpty
                                                                onChange={(e) => {
                                                                    const next = [...formData.dailyMetrics.tarps];
                                                                    next[mobileDay] = e.target.value;
                                                                    handleNestedInputChange('dailyMetrics', 'tarps', next);
                                                                }}
                                                            >
                                                                <MenuItem value=""><em>-</em></MenuItem>
                                                                {TARP_VALUES.map((opt) => (
                                                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                )}

                                                {(isAll || isVans || isFlatbeds) && (
                                                    <Box sx={metricRowSx}>
                                                        <Box>
                                                            <Typography variant="body2">Extra drop/pickup</Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                *Additional Docks for TG Lanes
                                                            </Typography>
                                                        </Box>
                                                        <TextField
                                                            value={formData.dailyMetrics.extraDropPickup[mobileDay] ?? ''}
                                                            onChange={(e) => {
                                                                const next = [...formData.dailyMetrics.extraDropPickup];
                                                                next[mobileDay] = e.target.value.replace(/[^0-9]/g,'');
                                                                handleNestedInputChange('dailyMetrics', 'extraDropPickup', next);
                                                            }}
                                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                            size="small"
                                                            sx={controlSx}
                                                        />
                                                    </Box>
                                                )}

                                                {(isAll || isVans) && (
                                                    <Box sx={metricRowSx}>
                                                        <Box>
                                                            <Typography variant="body2">WW Wood Cabinet Drops</Typography>
                                                        </Box>
                                                        <TextField
                                                            value={formData.dailyMetrics.wwWoodCabinetDrops[mobileDay] ?? ''}
                                                            onChange={(e) => {
                                                                const next = [...formData.dailyMetrics.wwWoodCabinetDrops];
                                                                next[mobileDay] = e.target.value.replace(/[^0-9]/g,'');
                                                                handleNestedInputChange('dailyMetrics', 'wwWoodCabinetDrops', next);
                                                            }}
                                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                            size="small"
                                                            sx={controlSx}
                                                        />
                                                    </Box>
                                                )}

                                                {(isAll || isVans || isFlatbeds) && (
                                                    <Box sx={metricRowSx}>
                                                        <Box>
                                                            <Typography variant="body2">Pre-Load (Not Delivering)</Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                *Pickup & drop within 160 miles of yard/securement & trailer move from lot to lot
                                                            </Typography>
                                                        </Box>
                                                        <TextField
                                                            value={formData.dailyMetrics.preLoadNotDelivering[mobileDay] ?? ''}
                                                            onChange={(e) => {
                                                                const next = [...formData.dailyMetrics.preLoadNotDelivering];
                                                                next[mobileDay] = e.target.value.replace(/[^0-9]/g,'');
                                                                handleNestedInputChange('dailyMetrics', 'preLoadNotDelivering', next);
                                                            }}
                                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                            size="small"
                                                            sx={controlSx}
                                                        />
                                                    </Box>
                                                )}

                                                {(isAll || isFlatbeds) && (
                                                    <Box sx={metricRowSx}>
                                                        <Box>
                                                            <Typography variant="body2">Oversize/Truss</Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                *with permits
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ justifySelf: isTightLayout ? 'start' : 'end' }}>
                                                            <Checkbox
                                                                checked={Boolean(formData.dailyMetrics.oversizeTruss[mobileDay])}
                                                                onChange={(e) => {
                                                                    const next = [...formData.dailyMetrics.oversizeTruss];
                                                                    next[mobileDay] = e.target.checked;
                                                                    handleNestedInputChange('dailyMetrics', 'oversizeTruss', next);
                                                                }}
                                                                size="small"
                                                                sx={{ p: 0 }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )}

                                                {(isAll || isFlatbeds) && (
                                                    <>
                                                        <Box id={`metric-shortRun-day-${mobileDay}`} sx={metricRowSx}>
                                                            <Box>
                                                                <Typography variant="body2">Short Run (Delivering)</Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                    *Pickup & delivery locations are within 325 miles of each other
                                                                </Typography>
                                                            </Box>
                                                            <FormControl size="small" error={Boolean(shortRunYesErrors[mobileDay])} sx={controlSx}>
                                                                <Select
                                                                    value={formData.dailyMetrics.shortRunYes[mobileDay] ?? ''}
                                                                    displayEmpty
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        const yesArr = [...formData.dailyMetrics.shortRunYes];
                                                                        yesArr[mobileDay] = val;
                                                                        handleNestedInputChange('dailyMetrics', 'shortRunYes', yesArr);
                                                                        if (val !== 'yes') {
                                                                            const orders = [...formData.dailyMetrics.shortRunOrders];
                                                                            orders[mobileDay] = [];
                                                                            handleNestedInputChange('dailyMetrics', 'shortRunOrders', orders);
                                                                        } else {
                                                                            const orders = [...formData.dailyMetrics.shortRunOrders];
                                                                            const dayOrders = Array.isArray(orders[mobileDay]) ? [...orders[mobileDay]] : [];
                                                                            if (dayOrders.length === 0) {
                                                                                dayOrders.push('');
                                                                                orders[mobileDay] = dayOrders;
                                                                                handleNestedInputChange('dailyMetrics', 'shortRunOrders', orders);
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    <MenuItem value=""><em>-</em></MenuItem>
                                                                    <MenuItem value="yes">Yes</MenuItem>
                                                                    <MenuItem value="no">No</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                        </Box>
                                                        {formData.dailyMetrics.shortRunYes[mobileDay] === 'yes' && (
                                                            <Box id={`metric-shortRun-orders-day-${mobileDay}`} sx={{ mt: 1, width: '100%', minWidth: 0 }}>
                                                                <Typography variant="caption" color="text.secondary">Order Numbers</Typography>
                                                                <Stack spacing={0.5} sx={{ mt: 0.5, width: '100%' }}>
                                                                    {(formData.dailyMetrics.shortRunOrders[mobileDay] || []).map((ord, i) => (
                                                                        <TextField
                                                                            key={i}
                                                                            fullWidth
                                                                            value={ord ?? ''}
                                                                            onChange={(e) => {
                                                                                const orders = [...formData.dailyMetrics.shortRunOrders];
                                                                                const dayOrders = Array.isArray(orders[mobileDay]) ? [...orders[mobileDay]] : [];
                                                                                dayOrders[i] = e.target.value.replace(/[^0-9]/g, '');
                                                                                orders[mobileDay] = dayOrders;
                                                                                handleNestedInputChange('dailyMetrics', 'shortRunOrders', orders);
                                                                            }}
                                                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                                            placeholder="order #"
                                                                            size="small"
                                                                            sx={{ maxWidth: '100%' }}
                                                                        />
                                                                    ))}
                                                                    <Button size="small" onClick={() => {
                                                                        const orders = [...formData.dailyMetrics.shortRunOrders];
                                                                        const dayOrders = Array.isArray(orders[mobileDay]) ? [...orders[mobileDay]] : [];
                                                                        dayOrders.push('');
                                                                        orders[mobileDay] = dayOrders;
                                                                        handleNestedInputChange('dailyMetrics', 'shortRunOrders', orders);
                                                                    }}>Add Order #</Button>
                                                                </Stack>
                                                            </Box>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        );
                                    })()}

                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Totals</Typography>
                                    <Grid container spacing={1}>
                                        {(isAll || isVans || isFlatbeds) && (
                                            <Grid item xs={6}><Typography variant="body2">Nights Out</Typography></Grid>
                                        )}
                                        {(isAll || isVans || isFlatbeds) && (
                                            <Grid item xs={6}><Typography align="right" variant="body2" fontWeight={700}>{formData.dailyMetrics.nightsOut.filter(Boolean).length}</Typography></Grid>
                                        )}
                                        {(isAll || isFlatbeds) && (
                                            <>
                                                <Grid item xs={6}><Typography variant="body2">Tarps</Typography></Grid>
                                                <Grid item xs={6}><Typography align="right" variant="body2" fontWeight={700}>{formatTarpTotal(sumTarps(formData.dailyMetrics.tarps))}</Typography></Grid>
                                            </>
                                        )}
                                        {(isAll || isVans || isFlatbeds) && (
                                            <>
                                                <Grid item xs={6}><Typography variant="body2">Extra drop/pickup</Typography></Grid>
                                                <Grid item xs={6}><Typography align="right" variant="body2" fontWeight={700}>{formData.dailyMetrics.extraDropPickup.reduce((t,v)=>t+(parseInt(v,10)||0),0)}</Typography></Grid>
                                            </>
                                        )}
                                        {(isAll || isVans) && (
                                            <>
                                                <Grid item xs={6}><Typography variant="body2">WW Wood Cabinet Drops</Typography></Grid>
                                                <Grid item xs={6}><Typography align="right" variant="body2" fontWeight={700}>{formData.dailyMetrics.wwWoodCabinetDrops.reduce((t,v)=>t+(parseInt(v,10)||0),0)}</Typography></Grid>
                                            </>
                                        )}
                                        {(isAll || isVans || isFlatbeds) && (
                                            <>
                                                <Grid item xs={6}><Typography variant="body2">Pre-Load (Not Delivering)</Typography></Grid>
                                                <Grid item xs={6}><Typography align="right" variant="body2" fontWeight={700}>{formData.dailyMetrics.preLoadNotDelivering.reduce((t,v)=>t+(parseInt(v,10)||0),0)}</Typography></Grid>
                                            </>
                                        )}
                                        {(isAll || isFlatbeds) && (
                                            <>
                                                <Grid item xs={6}><Typography variant="body2">Oversize/Truss</Typography></Grid>
                                                <Grid item xs={6}><Typography align="right" variant="body2" fontWeight={700}>{formData.dailyMetrics.oversizeTruss.filter(Boolean).length}</Typography></Grid>
                                            </>
                                        )}
                                        {(isAll || isFlatbeds) && (
                                            <>
                                                <Grid item xs={6}><Typography variant="body2">Short Run (Delivering)</Typography></Grid>
                                                <Grid item xs={6}><Typography align="right" variant="body2" fontWeight={700}>{(formData.dailyMetrics.shortRunOrders || []).reduce((t, arr) => t + (Array.isArray(arr) ? arr.filter(o => /\d+/.test(String(o))).length : 0), 0)}</Typography></Grid>
                                            </>
                                        )}
                                    </Grid>
                                </Box>
                            ) : (
                                <TableContainer sx={{ 
                                    overflowX: 'auto', 
                                    pb: 1,
                                    scrollbarWidth: 'auto',
                                    scrollbarColor: 'rgba(0,0,0,0.35) rgba(0,0,0,0.08)',
                                    '&::-webkit-scrollbar': { height: 12 },
                                    '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.08)', borderRadius: 8 },
                                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.35)', borderRadius: 8, border: '3px solid rgba(0,0,0,0.08)' }
                                }}>
                                <Table size="small" sx={{ minWidth: 900 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2, fontWeight: 700 }}>Metric</TableCell>
                                            {dayLabels.map(d => (
                                                <TableCell key={d} align="center" sx={{ fontWeight: 700 }}>{d}</TableCell>
                                            ))}
                                            <TableCell align="center" sx={{ fontWeight: 700 }}>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {/* Nights Out (boolean) */}
                                        {(isAll || isVans || isFlatbeds) && (
                                        <TableRow>
                                            <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>Nights Out</TableCell>
                                            {formData.dailyMetrics.nightsOut.map((val, idx) => (
                                                <TableCell key={idx} align="center">
                                                    <Checkbox 
                                                        checked={Boolean(val)}
                                                        onChange={(e) => {
                                                            const next = [...formData.dailyMetrics.nightsOut];
                                                            next[idx] = e.target.checked;
                                                            handleNestedInputChange('dailyMetrics', 'nightsOut', next);
                                                        }}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            ))}
                                            <TableCell align="center"><strong>{formData.dailyMetrics.nightsOut.filter(Boolean).length}</strong></TableCell>
                                        </TableRow>
                                        )}

                                        {/* Tarps (0 / 1/2 / 1 / 1 1/2 / 2) */}
                                        {(isAll || isFlatbeds) && (
                                        <TableRow>
                                            <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>Tarps</TableCell>
                                            {formData.dailyMetrics.tarps.map((val, idx) => (
                                                <TableCell key={idx} align="center">
                                                    <FormControl id={`metric-tarps-day-${idx}`} size="small" error={Boolean(tarpsErrors[idx])} sx={{ minWidth: 88 }}>
                                                        <Select
                                                            value={val ?? ''}
                                                            displayEmpty
                                                            onChange={(e) => {
                                                                const next = [...formData.dailyMetrics.tarps];
                                                                next[idx] = e.target.value;
                                                                handleNestedInputChange('dailyMetrics', 'tarps', next);
                                                            }}
                                                        >
                                                            <MenuItem value=""><em>-</em></MenuItem>
                                                            {TARP_VALUES.map((opt) => (
                                                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                            ))}
                                            <TableCell align="center"><strong>{formatTarpTotal(sumTarps(formData.dailyMetrics.tarps))}</strong></TableCell>
                                        </TableRow>
                                        )}

                                        {/* Extra drop/pickup (number) */}
                                        {(isAll || isVans || isFlatbeds) && (
                                        <TableRow>
                                            <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                                <Box>
                                                    <Typography variant="body2">Extra drop/pickup</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        *Additional Docks for TG Lanes
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {formData.dailyMetrics.extraDropPickup.map((val, idx) => (
                                                <TableCell key={idx} align="center">
                                                    <TextField
                                                        value={val ?? ''}
                                                        onChange={(e) => {
                                                            const next = [...formData.dailyMetrics.extraDropPickup];
                                                            next[idx] = e.target.value.replace(/[^0-9]/g,'');
                                                            handleNestedInputChange('dailyMetrics', 'extraDropPickup', next);
                                                        }}
                                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                        size="small"
                                                        sx={{ width: 64 }}
                                                    />
                                                </TableCell>
                                            ))}
                                            <TableCell align="center"><strong>{formData.dailyMetrics.extraDropPickup.reduce((t,v)=>t+(parseInt(v,10)||0),0)}</strong></TableCell>
                                        </TableRow>
                                        )}

                                        {/* WW Wood Cabinet Drops (number) */}
                                        {(isAll || isVans) && (
                                        <TableRow>
                                            <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>WW Wood Cabinet Drops</TableCell>
                                            {formData.dailyMetrics.wwWoodCabinetDrops.map((val, idx) => (
                                                <TableCell key={idx} align="center">
                                                    <TextField
                                                        value={val ?? ''}
                                                        onChange={(e) => {
                                                            const next = [...formData.dailyMetrics.wwWoodCabinetDrops];
                                                            next[idx] = e.target.value.replace(/[^0-9]/g,'');
                                                            handleNestedInputChange('dailyMetrics', 'wwWoodCabinetDrops', next);
                                                        }}
                                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                        size="small"
                                                        sx={{ width: 64 }}
                                                    />
                                                </TableCell>
                                            ))}
                                            <TableCell align="center"><strong>{formData.dailyMetrics.wwWoodCabinetDrops.reduce((t,v)=>t+(parseInt(v,10)||0),0)}</strong></TableCell>
                                        </TableRow>
                                        )}

                                        {/* Pre-Load (Not delivering) number */}
                                        {(isAll || isVans || isFlatbeds) && (
                                        <TableRow>
                                            <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                                <Box>
                                                    <Typography variant="body2">Pre-Load (Not Delivering)</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        *Pickup & drop within 160 miles of yard/securement & trailer move from lot to lot
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {formData.dailyMetrics.preLoadNotDelivering.map((val, idx) => (
                                                <TableCell key={idx} align="center">
                                                    <TextField
                                                        value={val ?? ''}
                                                        onChange={(e) => {
                                                            const next = [...formData.dailyMetrics.preLoadNotDelivering];
                                                            next[idx] = e.target.value.replace(/[^0-9]/g,'');
                                                            handleNestedInputChange('dailyMetrics', 'preLoadNotDelivering', next);
                                                        }}
                                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                        size="small"
                                                        sx={{ width: 64 }}
                                                    />
                                                </TableCell>
                                            ))}
                                            <TableCell align="center"><strong>{formData.dailyMetrics.preLoadNotDelivering.reduce((t,v)=>t+(parseInt(v,10)||0),0)}</strong></TableCell>
                                        </TableRow>
                                        )}

                                        {/* Oversize/Truss (boolean) */}
                                        {(isAll || isFlatbeds) && (
                                        <TableRow>
                                            <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                                <Box>
                                                    <Typography variant="body2">Oversize/Truss</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        *with permits
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {formData.dailyMetrics.oversizeTruss.map((val, idx) => (
                                                <TableCell key={idx} align="center">
                                                    <Checkbox 
                                                        checked={Boolean(val)}
                                                        onChange={(e) => {
                                                            const next = [...formData.dailyMetrics.oversizeTruss];
                                                            next[idx] = e.target.checked;
                                                            handleNestedInputChange('dailyMetrics', 'oversizeTruss', next);
                                                        }}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            ))}
                                            <TableCell align="center"><strong>{formData.dailyMetrics.oversizeTruss.filter(Boolean).length}</strong></TableCell>
                                        </TableRow>
                                        )}

                                        {/* Short Run (Delivering) Yes/No with orders */}
                                        {(isAll || isFlatbeds) && (
                                        <TableRow>
                                            <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                                <Box>
                                                    <Typography variant="body2">Short Run (Delivering)</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        *Pickup & delivery locations are within 325 miles of each other
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {formData.dailyMetrics.shortRunYes.map((val, idx) => (
                                                <TableCell key={idx} align="center">
                                                    <Stack spacing={0.5} alignItems="center">
                                                        <FormControl id={`metric-shortRun-day-${idx}`} size="small" error={Boolean(shortRunYesErrors[idx])} sx={{ minWidth: 64 }}>
                                                            <Select
                                                                value={val ?? ''}
                                                                displayEmpty
                                                                onChange={(e) => {
                                                                    const yesArr = [...formData.dailyMetrics.shortRunYes];
                                                                    yesArr[idx] = e.target.value;
                                                                    handleNestedInputChange('dailyMetrics', 'shortRunYes', yesArr);
                                                                    if (e.target.value !== 'yes') {
                                                                        const orders = [...formData.dailyMetrics.shortRunOrders];
                                                                        orders[idx] = [];
                                                                        handleNestedInputChange('dailyMetrics', 'shortRunOrders', orders);
                                                                    } else {
                                                                        const orders = [...formData.dailyMetrics.shortRunOrders];
                                                                        const dayOrders = Array.isArray(orders[idx]) ? [...orders[idx]] : [];
                                                                        if (dayOrders.length === 0) {
                                                                            dayOrders.push('');
                                                                            orders[idx] = dayOrders;
                                                                            handleNestedInputChange('dailyMetrics', 'shortRunOrders', orders);
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <MenuItem value=""><em>-</em></MenuItem>
                                                                <MenuItem value="yes">Yes</MenuItem>
                                                                <MenuItem value="no">No</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                        {val === 'yes' && (
                                                            <>
                                                                {(formData.dailyMetrics.shortRunOrders[idx] || []).map((ord, j) => (
                                                                    <TextField
                                                                        key={j}
                                                                        value={ord ?? ''}
                                                                        onChange={(e) => {
                                                                            const orders = [...formData.dailyMetrics.shortRunOrders];
                                                                            const dayOrders = Array.isArray(orders[idx]) ? [...orders[idx]] : [];
                                                                            dayOrders[j] = e.target.value.replace(/[^0-9]/g, '');
                                                                            orders[idx] = dayOrders;
                                                                            handleNestedInputChange('dailyMetrics', 'shortRunOrders', orders);
                                                                        }}
                                                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                                        placeholder="order #"
                                                                        size="small"
                                                                        sx={{ width: 72 }}
                                                                    />
                                                                ))}
                                                                <Button size="small" onClick={() => {
                                                                    const orders = [...formData.dailyMetrics.shortRunOrders];
                                                                    const dayOrders = Array.isArray(orders[idx]) ? [...orders[idx]] : [];
                                                                    dayOrders.push('');
                                                                    orders[idx] = dayOrders;
                                                                    handleNestedInputChange('dailyMetrics', 'shortRunOrders', orders);
                                                                }}>Add Order #</Button>
                                                            </>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            ))}
                                            <TableCell align="center"><strong>{(formData.dailyMetrics.shortRunOrders || []).reduce((t, arr) => t + (Array.isArray(arr) ? arr.filter(o => /\d+/.test(String(o))).length : 0), 0)}</strong></TableCell>
                                        </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                    {/* Operational Details */}
                    <Card sx={{ mt: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                                Operational Details
                            </Typography>
                            <Grid container spacing={2}>
                                {(isAll || isFlatbeds) && (
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Total Loaded Oversize Miles"
                                            value={formData.oversizeLoadedMiles ?? ''}
                                            onChange={(e) => handleInputChange('oversizeLoadedMiles', e.target.value)}
                                            inputProps={{ min: 0 }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12} md={4}>
                                    <FormControlLabel
                                        control={<Checkbox checked={formData.dotInspectionCompleted ?? false} onChange={(e) => handleInputChange('dotInspectionCompleted', e.target.checked)} />}
                                        label="DOT Inspection Completed"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(formData.hasReimbursement)}
                                                onChange={(e) => handleInputChange('hasReimbursement', e.target.checked)}
                                            />
                                        }
                                        label="I have reimbursements this week"
                                    />
                                </Grid>
                                {formData.hasReimbursement && (
                                    <>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                required
                                                type="number"
                                                label="Cash Receipts / Reimbursement"
                                                value={formData.cashReceiptsAmount ?? ''}
                                                onChange={(e) => handleInputChange('cashReceiptsAmount', e.target.value)}
                                                inputProps={{ min: 0, step: '0.01' }}
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{ startAdornment: <span style={{ marginRight: 6 }}>$</span> }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Cash Receipts Note"
                                                value={formData.cashReceiptsNote ?? ''}
                                                onChange={(e) => handleInputChange('cashReceiptsNote', e.target.value)}
                                                helperText="* Name & Truck # must be on receipts and who authorized"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Receipt photos *
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                On a phone this opens your photo library (you can also take a new picture). JPEG, PNG, WebP, or HEIC, max 10 files, 10MB each.
                                            </Typography>
                                            <input
                                                ref={receiptInputRef}
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                hidden
                                                onChange={(e) => handleReceiptFiles(e.target.files)}
                                            />
                                            <Button
                                                variant="outlined"
                                                startIcon={receiptUploading ? <CircularProgress size={16} /> : <PhotoCamera />}
                                                onClick={() => receiptInputRef.current && receiptInputRef.current.click()}
                                                disabled={receiptUploading || receiptAttachments.length >= 10}
                                            >
                                                {receiptUploading ? 'Uploading…' : 'Add receipt photos'}
                                            </Button>
                                            {receiptError && (
                                                <Alert severity="error" sx={{ mt: 1 }}>{receiptError}</Alert>
                                            )}
                                            <ReceiptAttachmentGallery
                                                attachments={receiptAttachments}
                                                ownerId={draftId || editId}
                                                kind={draftId ? 'draft' : 'checklist'}
                                                onRemove={draftId ? handleRemoveReceipt : undefined}
                                                removingId={receiptRemovingId}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>

                            {/* Breakdown pay */}
                            <Box sx={{ mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={Boolean(formData.hasBreakdownPay)}
                                            onChange={(e) => handleInputChange('hasBreakdownPay', e.target.checked)}
                                        />
                                    }
                                    label="Breakdown pay this week"
                                />
                                {formData.hasBreakdownPay && (
                                    <>
                                        {(formData.breakdownEvents || []).map((ev, idx) => (
                                            <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="center">
                                                <Grid item xs={12} sm={5} md={4}>
                                                    <DatePicker
                                                        label="Date"
                                                        value={ev.date ? new Date(ev.date) : null}
                                                        onChange={(v) => {
                                                            const next = [...(formData.breakdownEvents || [])];
                                                            next[idx] = { ...next[idx], date: v };
                                                            handleInputChange('breakdownEvents', next);
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={10} sm={5} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        required
                                                        type="time"
                                                        label="Time of Notification"
                                                        value={ev.notificationTime || ''}
                                                        onChange={(e) => {
                                                            const next = [...(formData.breakdownEvents || [])];
                                                            next[idx] = { ...next[idx], notificationTime: e.target.value };
                                                            handleInputChange('breakdownEvents', next);
                                                        }}
                                                        InputLabelProps={{ shrink: true }}
                                                        inputProps={{ step: 300 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={2} sm={2} md={2}>
                                                    <IconButton
                                                        aria-label="Remove breakdown"
                                                        onClick={() => {
                                                            const next = (formData.breakdownEvents || []).filter((_, i) => i !== idx);
                                                            handleInputChange('breakdownEvents', next);
                                                        }}
                                                    >
                                                        <DeleteOutline />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        ))}
                                        <Button
                                            size="small"
                                            onClick={() => handleInputChange('breakdownEvents', [
                                                ...(formData.breakdownEvents || []),
                                                { date: null, notificationTime: '' }
                                            ])}
                                        >
                                            Add breakdown
                                        </Button>
                                    </>
                                )}
                            </Box>

                            {/* Detention */}
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Detention</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    * Notify dispatch at the one-hour mark. Ensure in/out times are recorded on BOLs. Do not arrive late to your dispatched time.
                                </Typography>
                                {(formData.detentionEvents || []).map((ev, idx) => (
                                    <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                                        <Grid item xs={12} md={3}>
                                            <DatePicker
                                                label="Date"
                                                value={ev.date ? new Date(ev.date) : null}
                                                onChange={(v) => {
                                                    const next = [...(formData.detentionEvents || [])];
                                                    next[idx] = { ...next[idx], date: v };
                                                    handleInputChange('detentionEvents', next);
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <CityStateAutocomplete
                                                label="Location"
                                                value={ev.location || ''}
                                                onChange={(text) => {
                                                    const next = [...(formData.detentionEvents || [])];
                                                    next[idx] = { ...next[idx], location: text };
                                                    handleInputChange('detentionEvents', next);
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <TextField
                                                fullWidth
                                                type="time"
                                                label="Arrival Time"
                                                value={ev.arrivalTime || ''}
                                                onChange={(e) => {
                                                    const next = [...(formData.detentionEvents || [])];
                                                    next[idx] = { ...next[idx], arrivalTime: e.target.value };
                                                    handleInputChange('detentionEvents', next);
                                                }}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ step: 300 }}
                                            />
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <TextField
                                                fullWidth
                                                type="time"
                                                label="Depart Time"
                                                value={ev.departTime || ''}
                                                onChange={(e) => {
                                                    const next = [...(formData.detentionEvents || [])];
                                                    next[idx] = { ...next[idx], departTime: e.target.value };
                                                    handleInputChange('detentionEvents', next);
                                                }}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ step: 300 }}
                                            />
                                        </Grid>
                                    </Grid>
                                ))}
                                <Button size="small" onClick={() => handleInputChange('detentionEvents', [ ...(formData.detentionEvents || []), { date: null, location: '', arrivalTime: '', departTime: '' } ])}>Add Detention</Button>
                            </Box>

                            {/* Weekend Pay */}
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Weekend Pay</Typography>
                                <FormControl component="fieldset" sx={{ mt: 0.5, width: '100%' }}>
                                    <FormLabel component="legend" sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
                                        If dispatched out over a weekend for this pay period, please check the appropriate box below:
                                    </FormLabel>
                                    <RadioGroup
                                        value={formData.weekendPay === 'half' || formData.weekendPay === 'full' ? formData.weekendPay : ''}
                                        onChange={(e) => handleInputChange('weekendPay', e.target.value)}
                                    >
                                        <FormControlLabel
                                            value="half"
                                            control={
                                                <Radio
                                                    onClick={() => {
                                                        if (formData.weekendPay === 'half') {
                                                            handleInputChange('weekendPay', 'none');
                                                        }
                                                    }}
                                                />
                                            }
                                            label="½ WEEKEND: Driver was still under dispatch overnight Saturday, RETURNED SUNDAY"
                                        />
                                        <FormControlLabel
                                            value="full"
                                            control={
                                                <Radio
                                                    onClick={() => {
                                                        if (formData.weekendPay === 'full') {
                                                            handleInputChange('weekendPay', 'none');
                                                        }
                                                    }}
                                                />
                                            }
                                            label="FULL WEEKEND: Driver was still under dispatch overnight Sunday, RETURNED MONDAY OR LATER"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Box>

                            {/* Weekly Loads */}
                            <Box sx={{ mt: 3, border: highlightWeeklyLoads ? '1px solid' : 'none', borderColor: highlightWeeklyLoads ? 'error.main' : 'transparent', borderRadius: 1, p: highlightWeeklyLoads ? 1 : 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: highlightWeeklyLoads ? 'error.main' : 'inherit' }}>Weekly Loads</Typography>
                                {(formData.weeklyLoads || []).map((row, idx) => (
                                    <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="flex-start">
                                        <Grid item xs={12} md={isTightLayout ? 12 : 3}>
                                            <DatePicker
                                                label="Date"
                                                closeOnSelect
                                                value={row.date ? new Date(row.date) : null}
                                                onChange={(v) => {
                                                    const next = [ ...(formData.weeklyLoads || []) ];
                                                    next[idx] = { ...next[idx], date: v };
                                                    handleInputChange('weeklyLoads', next);
                                                }}
                                                slotProps={{
                                                    actionBar: { actions: [] },
                                                    textField: { fullWidth: true, error: weeklyLoadsErrors[idx]?.date, helperText: weeklyLoadsErrors[idx]?.date ? 'Required' : '' }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={isTightLayout ? 12 : 3}>
                                            <TextField
                                                fullWidth
                                                label="ELD Order #"
                                                required
                                                value={row.eldOrderNumber || ''}
                                                onChange={(e) => {
                                                    const next = [ ...(formData.weeklyLoads || []) ];
                                                    next[idx] = { ...next[idx], eldOrderNumber: e.target.value.replace(/[^0-9]/g, '') };
                                                    handleInputChange('weeklyLoads', next);
                                                }}
                                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                error={weeklyLoadsErrors[idx]?.eldOrderNumber}
                                                helperText={weeklyLoadsErrors[idx]?.eldOrderNumber ? 'Required' : ''}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={isTightLayout ? 12 : 3}>
                                            <CityStateAutocomplete
                                                label="Pickup City/State"
                                                value={row.from || ''}
                                                onChange={(text) => {
                                                    const next = [ ...(formData.weeklyLoads || []) ];
                                                    next[idx] = { ...next[idx], from: text };
                                                    handleInputChange('weeklyLoads', next);
                                                }}
                                                error={weeklyLoadsErrors[idx]?.from}
                                                helperText={weeklyLoadsErrors[idx]?.from ? 'Required' : ''}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={isTightLayout ? 12 : 3}>
                                            <CityStateAutocomplete
                                                label="Delivery City/State"
                                                value={row.to || ''}
                                                onChange={(text) => {
                                                    const next = [ ...(formData.weeklyLoads || []) ];
                                                    next[idx] = { ...next[idx], to: text };
                                                    handleInputChange('weeklyLoads', next);
                                                }}
                                                error={weeklyLoadsErrors[idx]?.to}
                                                helperText={weeklyLoadsErrors[idx]?.to ? 'Required' : ''}
                                            />
                                        </Grid>
                                    </Grid>
                                ))}
                                <Button size="small" onClick={() => {
                                    setHighlightWeeklyLoads(false);
                                    setWeeklyLoadsErrors([ ...(weeklyLoadsErrors || []), { date: false, eldOrderNumber: false, from: false, to: false } ]);
                                    handleInputChange('weeklyLoads', [ ...(formData.weeklyLoads || []), { date: null, eldOrderNumber: '', from: '', to: '' } ])
                                }}>Add Load</Button>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mt: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                                Comments / Notes
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={8}
                                label="Comments / Notes"
                                value={formData.additionalNotes || ''}
                                onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                                inputProps={{ maxLength: 1000 }}
                                helperText={`${(formData.additionalNotes || '').length}/1000`}
                            />
                        </CardContent>
                    </Card>

                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setFormOpen(false);
                                        setError('');
                                        setSuccess('');
                                        setInfo('');
                                    }}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                                >
                                    {loading ? 'Submitting...' : 'Submit Final'}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                    </Box>
                </Paper>
            </Collapse>
        </Paper>
    );
};

export default WeeklyChecklistForm; 